import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../../state/state';
import type { RootState } from '../../../../state/state';
import { selectActiveRole, selectEffectiveUID, selectChequeredEnabled } from '../../../../state/UserStatesV2/userSliceV2';
import { getPuzzlesForProvider, getBookmarkedPuzzles, bookmarkPuzzle, removeBookmark, deletePuzzle, deleteAllPuzzles, ChessPuzzle, getPuzzlesForSubscription, addPuzzleToSubscription, removePuzzleFromSubscription, getPuzzlesInSubscription, markPuzzleAsSolved, markPuzzleAsTaught, unmarkPuzzleAsTaught, bulkAddPuzzlesToSubscription } from '../../../../services/chessPuzzleService';
import { toast } from 'react-toastify';
import ChequeredChess from '../SubscriptionDetails/Components/ChequeredChess';
import { createCollaborativeSession, joinCollaborativeSession, CollaborativePuzzleSession, getActiveSessions, subscribeToSession } from '../../../../services/collaborativePuzzleService';
import CollaborativePuzzleView from '../SubscriptionDetails/Components/CollaborativePuzzleView';
import { Room } from 'livekit-client';
import { motion } from 'framer-motion';
import { seedPuzzlesToDatabase, seedAdvancedPuzzlesFromCSVContent } from '../../../../utils/seedChessPuzzles';
import { importLichessCSVFile } from '../../../../utils/importLichessCSV';
import { downloadPuzzleTemplate, downloadCSVTemplate, importPuzzlesFromExcel, importPuzzlesFromCSVFile } from '../../../../utils/excelPuzzleImporter';
import AnalyzeGameView from './AnalyzeGameView';
import ChessBasicsView from './ChessBasicsView';
import CreatePuzzleView from './CreatePuzzleView';
import { db } from '../../../../Config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import ChequeredPromotion from '../SubscriptionDetails/Components/ChequeredPromotion';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SubscriptionDocWithId } from '../../types/SubscriptionType';
import { TEACHING_PRESET_POSITIONS } from '../../../../data/teachingPresetPositions';
import {
    trackChequeredPageViewed,
    trackChequeredCategorySelected,
    trackChequeredPuzzleOpened,
    trackChequeredPuzzleSolved,
    trackChequeredPuzzleBookmarked,
    trackChequeredSeedPuzzlesClicked,
    trackChequeredSeedPuzzlesCompleted,
    trackChequeredImportClicked,
    trackChequeredImportCompleted,
    trackChequeredTemplateDownloaded,
    trackChequeredClassPuzzlesViewed,
    trackChequeredPuzzleAddedToClass,
    trackChequeredPuzzleRemovedFromClass,
    trackChequeredPuzzleMarkedSolved,
    trackChequeredCollabSessionStarted
} from '../../../../utils/analytics';

type PuzzleCategory = 'tactics' | 'opening' | 'endgame' | 'castle' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' | 'checkmate' | 'defense' | 'custom' | 'teaching' | 'import' | 'create' | 'analyze';
type ComplexityLevel = 'all' | 'beginner' | 'intermediate' | 'advanced';

interface PuzzleListItemProps {
    puzzle: ChessPuzzle;
    isSelected: boolean;
    isBookmarked: boolean;
    onSelect: (puzzle: ChessPuzzle) => void;
    onBookmark: (puzzleId: string, isBookmarked: boolean) => void;
    onDelete?: (puzzleId: string) => void;
    canDelete?: boolean;
    onAddToSubscription?: (puzzleId: string) => void;
    isInSubscription?: boolean;
    showAddButton?: boolean;
    bulkSelectionMode?: boolean;
    isChecked?: boolean;
    onToggleCheck?: (puzzleId: string) => void;
}

const PuzzleListItem: React.FC<PuzzleListItemProps> = ({ puzzle, isSelected, isBookmarked, onSelect, onBookmark, onDelete, canDelete, onAddToSubscription, isInSubscription, showAddButton, bulkSelectionMode, isChecked, onToggleCheck }) => {
    const getRatingColor = (rating: number) => {
        if (rating < 1200) return 'text-green-600';
        if (rating < 1800) return 'text-yellow-600';
        return 'text-red-600';
    };

    // Parse moves to get move count
    const moveCount = puzzle.moves ? puzzle.moves.split(' ').filter((m: string) => m && !m.includes('.')).length : 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`puzzle-card cursor-pointer transition-all hover:shadow-lg ${
                isSelected || (bulkSelectionMode && isChecked)
                    ? 'border-purple-500 bg-purple-50 shadow-md' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            onClick={() => bulkSelectionMode && onToggleCheck ? onToggleCheck(puzzle.id) : onSelect(puzzle)}
            style={{
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: `2px solid ${(isSelected || (bulkSelectionMode && isChecked)) ? '#a855f7' : '#e5e7eb'}`,
                marginBottom: '0.5rem'
            }}
        >
            <div className="puzzle-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                {bulkSelectionMode && (
                    <input
                        type="checkbox"
                        checked={isChecked || false}
                        onChange={(e) => {
                            e.stopPropagation();
                            onToggleCheck?.(puzzle.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '1rem',
                            height: '1rem',
                            cursor: 'pointer',
                            accentColor: '#9333ea',
                            marginRight: '0.5rem'
                        }}
                    />
                )}
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', margin: 0, flex: 1, lineHeight: '1.3' }}>
                            {puzzle.description || `Puzzle #${puzzle.id.slice(0, 6)}`}
                </h3>
                        {puzzle.complexity && (
                    <span 
                        className={`difficulty-badge ${puzzle.complexity}`}
                        style={{
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            border: '1px solid',
                            ...(puzzle.complexity === 'beginner' ? { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' } :
                                puzzle.complexity === 'intermediate' ? { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde047' } :
                                { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' })
                        }}
                    >
                                {puzzle.complexity}
                            </span>
                        )}
                    </div>
            <p className="puzzle-description" style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', lineHeight: '1.3' }}>
                {puzzle.themes && puzzle.themes.length > 0 ? puzzle.themes.slice(0, 3).join(', ') : 'Chess puzzle'}
            </p>
            <div className="puzzle-meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#6b7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span className={`font-medium ${getRatingColor(puzzle.rating)}`}>
                            ⭐ {puzzle.rating}
                        </span>
                    <span>{moveCount} {moveCount === 1 ? 'move' : 'moves'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {canDelete && onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to delete this puzzle?')) {
                                    onDelete(puzzle.id);
                                }
                            }}
                            style={{
                                padding: '0.125rem',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#ef4444'
                            }}
                            title="Delete puzzle"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '1rem', height: '1rem' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    )}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onBookmark(puzzle.id, isBookmarked);
                    }}
                    style={{
                        padding: '0.125rem',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    title={isBookmarked ? 'Remove bookmark' : 'Bookmark puzzle'}
                >
                    {isBookmarked ? (
                        <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" style={{ width: '1rem', height: '1rem', color: '#eab308' }}>
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '1rem', height: '1rem', color: '#9ca3af' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                    )}
                </button>
                    {showAddButton && onAddToSubscription && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddToSubscription(puzzle.id);
                            }}
                            disabled={isInSubscription}
                            style={{
                                padding: '0.125rem 0.375rem',
                                background: isInSubscription ? '#10b981' : '#9333ea',
                                color: 'white',
                                border: 'none',
                                cursor: isInSubscription ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '0.25rem',
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                opacity: isInSubscription ? 0.7 : 1
                            }}
                            title={isInSubscription ? 'Already in subscription' : 'Add to subscription'}
                        >
                            {isInSubscription ? '✓ Added' : '+'}
                </button>
                    )}
            </div>
            </div>
        </motion.div>
    );
};

// Launch Animation and Feature Showcase moved to ChequeredLandingPage.tsx

// Feature flag: Set to true to show Import Puzzles button and dialog
const SHOW_IMPORT_PUZZLES = false;

const ChequeredPage: React.FC = () => {
    const { subscriptionId } = useParams<{ subscriptionId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = (location.state as any)?.from || '/mysubscriptions';
    const activeRole = useAppSelector(selectActiveRole);
    const serviceProviderId = useAppSelector(selectEffectiveUID);
    const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
    const loggedInUserId = userData?.uid || '';
    
    // Get chequeredEnabled flag from user state (fetched from active service provider)
    const chequeredEnabled = useAppSelector(selectChequeredEnabled);
    const isLoadingFeatureFlag = !userData; // Loading if userData is not yet available
    
    // Get subscriptions for selection dialog
    const subscriptionList: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice?.data ?? []);
    
    // Check if user is admin (for admin-only features like delete all)
    const isAdmin = !activeRole || activeRole?.role === 'Administrator';
    
    // State for subscription selection dialog
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
    const [selectedSubscriptionForPuzzle, setSelectedSubscriptionForPuzzle] = useState<string>('');
    const [pendingPuzzleId, setPendingPuzzleId] = useState<string | null>(null);
    const [activeSubscriptionForData, setActiveSubscriptionForData] = useState<string>(subscriptionId || '');
    
    // Detect mobile device
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileWarning, setShowMobileWarning] = useState(true);
    
    useEffect(() => {
        const checkMobile = () => {
            const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const smallScreen = window.innerWidth < 768;
            setIsMobile(mobile || smallScreen);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Determine which page we're on based on route
    const currentPage = location.pathname.split('/').pop() || 'puzzles';
    const isPuzzlesPage = currentPage === 'puzzles';
    const isNeoTeachingPage = currentPage === 'neo';
    const isTeachingPage = currentPage === 'teaching' || isNeoTeachingPage;
    const isSubscriptionDataPage = currentPage === 'subscription-data';
    const isAnalysisPage = currentPage === 'analysis';
    const isChessBasicsPage = currentPage === 'chess-basics';

    // Keep active subscription in sync with route param
    useEffect(() => {
        if (subscriptionId) {
            setActiveSubscriptionForData(subscriptionId);
        }
    }, [subscriptionId]);
    
    // State management (animation removed - only on landing page)
    const [activeCategory, setActiveCategory] = useState<PuzzleCategory>('tactics');
    const [selectedComplexity, setSelectedComplexity] = useState<ComplexityLevel>('all');
    const [puzzles, setPuzzles] = useState<ChessPuzzle[]>([]);
    const [loadingPuzzles, setLoadingPuzzles] = useState(false);
    const [selectedPuzzle, setSelectedPuzzle] = useState<ChessPuzzle | null>(null);
    const [showPuzzleModal, setShowPuzzleModal] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [bookmarkedPuzzleIds, setBookmarkedPuzzleIds] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isInMeeting, setIsInMeeting] = useState(false);
    const [room] = useState<Room | null>(null);
    const [seedingPuzzles, setSeedingPuzzles] = useState(false);
    const [seedingProgress, setSeedingProgress] = useState({ current: 0, total: 0 });
    const [totalPuzzleCount, setTotalPuzzleCount] = useState<number>(0);
    const [puzzleCountsByCategory, setPuzzleCountsByCategory] = useState<Record<string, number>>({}); // TODO: Display counts in category buttons
    
    // Reference to avoid unused variable warning (will be used for displaying counts)
    void puzzleCountsByCategory;
    
    // Subscription Data page state
    const [subscriptionPuzzles, setSubscriptionPuzzles] = useState<ChessPuzzle[]>([]);
    const [loadingSubscriptionPuzzles, setLoadingSubscriptionPuzzles] = useState(false);
    const [puzzlesInSubscription, setPuzzlesInSubscription] = useState<Set<string>>(new Set());
    const [subscriptionName, setSubscriptionName] = useState<string>('');
    const [importingPuzzles, setImportingPuzzles] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showCreatePuzzleModal, setShowCreatePuzzleModal] = useState(false);
    const [puzzleFilter, setPuzzleFilter] = useState<'all' | 'unsolved' | 'solved' | 'taught'>('all');
    const [importingExcel, setImportingExcel] = useState(false);
    const [excelImportProgress, setExcelImportProgress] = useState({ current: 0, total: 0 });
    const [showClassPuzzlesGuide, setShowClassPuzzlesGuide] = useState(false);
    
    // Collaborative puzzle solving state
    const [collaborativeSessionId, setCollaborativeSessionId] = useState<string | null>(null);
    const [showCollaborativeView, setShowCollaborativeView] = useState(false);
    const [collaborativePuzzle, setCollaborativePuzzle] = useState<ChessPuzzle | null>(null);
    const [activeSessions, setActiveSessions] = useState<{ [sessionId: string]: CollaborativePuzzleSession }>({});
    const unsubscribeActiveSessionsRef = React.useRef<(() => void) | null>(null);
    
    // Bulk selection state
    const [selectedPuzzleIds, setSelectedPuzzleIds] = useState<Set<string>>(new Set());
    const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
    
    // Teaching board preset position state
    const [selectedPresetPosition, setSelectedPresetPosition] = useState<string | null>(null);
    const [selectedPresetName, setSelectedPresetName] = useState<string | null>(null);
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [presetSearchQuery, setPresetSearchQuery] = useState('');

    // Feature showcase removed - no longer needed

    // Category configuration - Only puzzle categories (no teaching, import, create, analyze)
    const categories: Array<{ id: PuzzleCategory; label: string; icon: string; description: string }> = [
        { id: 'tactics', label: 'Tactical', icon: '🎯', description: 'Puzzles and combinations' },
        { id: 'opening', label: 'Opening', icon: '🚀', description: 'Opening theory and patterns' },
        { id: 'endgame', label: 'Endgame', icon: '🏁', description: 'Endgame techniques' },
        { id: 'middlegame', label: 'Middlegame', icon: '⚔️', description: 'Middlegame strategy and planning' },
        { id: 'strategy', label: 'Strategy', icon: '🧠', description: 'Positional play and strategic concepts' },
        { id: 'calculation', label: 'Calculation', icon: '🔢', description: 'Deep calculation and visualization' },
        { id: 'pattern', label: 'Pattern', icon: '🧩', description: 'Tactical patterns and motifs' },
        { id: 'checkmate', label: 'Checkmate', icon: '♔', description: 'Checkmating sequences' },
        { id: 'defense', label: 'Defense', icon: '🛡️', description: 'Defensive techniques' },
        { id: 'custom', label: 'Custom', icon: '✨', description: 'Your custom created puzzles' }
        // { id: 'castle', label: 'Castle', icon: '🏰', description: 'Castling puzzles' } // No castle puzzles available
    ];

    // Load puzzles from Firestore (skips loading for Teaching mode)
    const loadPuzzles = useCallback(async () => {
        if (!serviceProviderId) {
            console.warn('No service provider ID available');
            return;
        }

        // In teaching, import, create, analyze, or subscription-data mode, we don't load puzzles
        if (activeCategory === 'teaching' || activeCategory === 'import' || activeCategory === 'create' || activeCategory === 'analyze' || isTeachingPage || isSubscriptionDataPage || isAnalysisPage) {
            setPuzzles([]);
            setSelectedPuzzle(null);
            return;
        }


        setLoadingPuzzles(true);
        try {
            let loadedPuzzles: ChessPuzzle[] = [];

            // Special handling for 'custom' category - filter by source='custom'
            if (activeCategory === 'custom') {
                // Load all puzzles and filter by source='custom' client-side
                const filters: any = {
                    complexity: selectedComplexity !== 'all' ? selectedComplexity : undefined,
                    searchQuery: searchQuery || undefined
                };
                const allPuzzles = await getPuzzlesForProvider(serviceProviderId, filters);
                loadedPuzzles = allPuzzles.filter(puzzle => puzzle.source === 'custom');
            } else {
                // Regular category filtering
                const categoryMap: Record<PuzzleCategory, 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' | 'checkmate' | 'defense'> = {
                    tactics: 'tactics',
                    opening: 'opening',
                    endgame: 'endgame',
                    middlegame: 'middlegame',
                    strategy: 'strategy',
                    calculation: 'calculation',
                    pattern: 'pattern',
                    checkmate: 'checkmate',
                    defense: 'defense',
                    custom: 'tactics', // Not used - handled above
                    castle: 'tactics', // Fallback for backwards compatibility
                    teaching: 'tactics', // unused because we early-return above
                    import: 'tactics', // unused because we early-return above
                    create: 'tactics', // unused because we early-return above
                    analyze: 'tactics' // unused because we early-return above
                };

                const filters: any = {
                    category: categoryMap[activeCategory],
                    complexity: selectedComplexity !== 'all' ? selectedComplexity : undefined,
                    searchQuery: searchQuery || undefined
                    // No limit - load all puzzles for the category (~333 per category is manageable)
                };

                loadedPuzzles = await getPuzzlesForProvider(serviceProviderId, filters);
            }
            
            setPuzzles(loadedPuzzles);
            console.log(`📚 Loaded ${loadedPuzzles.length} puzzles for category: ${activeCategory}`);
        } catch (error) {
            console.error('Error loading puzzles:', error);
            toast.error('Failed to load puzzles');
            setPuzzles([]);
        } finally {
            setLoadingPuzzles(false);
        }
    }, [serviceProviderId, activeCategory, selectedComplexity, searchQuery, isTeachingPage, isSubscriptionDataPage, isAnalysisPage]);

    // Load bookmarks
    const loadBookmarks = useCallback(async () => {
        if (!serviceProviderId || !activeRole?.uid) return;

        try {
            const bookmarks = await getBookmarkedPuzzles(serviceProviderId, activeRole.uid);
            setBookmarkedPuzzleIds(bookmarks);
        } catch (error) {
            console.error('Error loading bookmarks:', error);
        }
    }, [serviceProviderId, activeRole?.uid]);

    // Load puzzles and bookmarks when filters change
    useEffect(() => {
            loadPuzzles();
    }, [loadPuzzles]);

    useEffect(() => {
            loadBookmarks();
    }, [loadBookmarks]);

    // Handle puzzle selection
    const handlePuzzleSelect = useCallback((puzzle: ChessPuzzle) => {
        console.log('🎯 Opening puzzle in fullscreen:', puzzle);
        if (serviceProviderId && loggedInUserId) {
            trackChequeredPuzzleOpened({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                subscription_id: subscriptionId,
                subscription_name: subscriptionName || undefined,
                user_name: activeRole?.displayName || undefined,
                user_role: activeRole?.role || undefined,
                puzzle_id: puzzle.id,
                puzzle_rating: puzzle.rating,
                puzzle_complexity: puzzle.complexity,
                puzzle_category: puzzle.category,
                source: 'fullscreen'
            });
        }
        navigate(`/chequered/${subscriptionId}/puzzle/${puzzle.id}`);
    }, [navigate, subscriptionId, serviceProviderId, loggedInUserId, subscriptionName, activeRole?.displayName, activeRole?.role]);

    // Handle bookmark toggle
    const handleBookmarkToggle = useCallback(async (puzzleId: string, isBookmarked: boolean) => {
        if (!serviceProviderId || !activeRole?.uid || !subscriptionId) return;

        try {
            if (isBookmarked) {
                await removeBookmark(serviceProviderId, puzzleId, activeRole.uid);
                setBookmarkedPuzzleIds(prev => prev.filter(id => id !== puzzleId));
                trackChequeredPuzzleBookmarked({
                    service_provider_id: serviceProviderId,
                    user_id: activeRole.uid,
                    subscription_id: subscriptionId,
                    subscription_name: subscriptionName || undefined,
                    user_name: activeRole?.displayName || undefined,
                    user_role: activeRole?.role || undefined,
                    puzzle_id: puzzleId,
                    action: 'remove'
                });
            } else {
                await bookmarkPuzzle(serviceProviderId, puzzleId, activeRole.uid, subscriptionId);
                setBookmarkedPuzzleIds(prev => [...prev, puzzleId]);
                trackChequeredPuzzleBookmarked({
                    service_provider_id: serviceProviderId,
                    user_id: activeRole.uid,
                    subscription_id: subscriptionId,
                    subscription_name: subscriptionName || undefined,
                    user_name: activeRole?.displayName || undefined,
                    user_role: activeRole?.role || undefined,
                    puzzle_id: puzzleId,
                    action: 'add'
                });
            }
        } catch (error) {
            console.error('Error toggling bookmark:', error);
        }
    }, [serviceProviderId, activeRole?.uid, subscriptionId, subscriptionName, activeRole?.displayName, activeRole?.role]);

    // Handle puzzle delete
    const handleDeletePuzzle = useCallback(async (puzzleId: string) => {
        if (!serviceProviderId) return;

        try {
            const success = await deletePuzzle(serviceProviderId, puzzleId);
            if (success) {
                // Remove from local state
                setPuzzles(prev => prev.filter(p => p.id !== puzzleId));
                // If deleted puzzle was selected, clear selection
                if (selectedPuzzle?.id === puzzleId) {
                    setSelectedPuzzle(null);
                }
                // Remove from bookmarks if bookmarked
                setBookmarkedPuzzleIds(prev => prev.filter(id => id !== puzzleId));
            }
        } catch (error) {
            console.error('Error deleting puzzle:', error);
        }
    }, [serviceProviderId, selectedPuzzle]);

    // Handle delete all puzzles (admin only)
    const handleDeleteAllPuzzles = useCallback(async () => {
        if (!serviceProviderId) return;

        if (!isAdmin) {
            toast.error('Only administrators can delete all puzzles');
            return;
        }

        if (!window.confirm('Are you sure you want to delete ALL puzzles? This action cannot be undone.')) {
            return;
        }

        try {
            const count = await deleteAllPuzzles(serviceProviderId);
            if (count > 0) {
                setPuzzles([]);
                setSelectedPuzzle(null);
                setBookmarkedPuzzleIds([]);
                // Reload puzzles to update the list and counts
                await loadPuzzles();
            }
        } catch (error) {
            console.error('Error deleting all puzzles:', error);
        }
    }, [serviceProviderId, activeRole, loadPuzzles]);

    // Check if we're in a LiveKit meeting (for real-time sharing)
    useEffect(() => {
        const checkMeetingContext = () => {
            return false;
        };
        setIsInMeeting(checkMeetingContext());
    }, []);

    // handleLaunchComplete removed - animation only on landing page

    // Load total puzzle count and counts by category
    const loadTotalPuzzleCount = useCallback(async () => {
        if (!serviceProviderId) return;
        try {
            const allPuzzles = await getPuzzlesForProvider(serviceProviderId);
            setTotalPuzzleCount(allPuzzles.length);
            
            // Count puzzles by category
            const counts: Record<string, number> = {
                'tactics': 0,
                'opening': 0,
                'endgame': 0,
                'middlegame': 0,
                'strategy': 0,
                'calculation': 0,
                'pattern': 0,
                'checkmate': 0,
                'defense': 0,
                'custom': 0
            };
            
            allPuzzles.forEach(puzzle => {
                if (puzzle.category in counts) {
                    counts[puzzle.category] = (counts[puzzle.category] || 0) + 1;
                }
                // Count custom puzzles separately by source
                if (puzzle.source === 'custom') {
                    counts['custom'] = (counts['custom'] || 0) + 1;
                }
            });
            
            setPuzzleCountsByCategory(counts);
        } catch (error) {
            console.error('Error loading puzzle count:', error);
        }
    }, [serviceProviderId]);

    // Seed puzzles to database
    const handleSeedPuzzles = useCallback(async () => {
        if (!serviceProviderId || !loggedInUserId) {
            toast.error('Cannot seed puzzles: Missing service provider ID or user ID');
            console.error('Missing IDs:', { serviceProviderId, loggedInUserId });
            return;
        }

        // Ask user if they want to delete old duplicate puzzles first
        const deleteOld = window.confirm(
            '🗑️ Delete old duplicate puzzles before seeding?\n\n' +
            '✅ YES = Delete old puzzles and seed fresh UNIQUE puzzles (recommended)\n' +
            '❌ NO = Keep old puzzles and add new ones (may have duplicates)\n\n' +
            'Click OK to delete old puzzles and get unique ones, or Cancel to keep old ones.'
        );

        trackChequeredSeedPuzzlesClicked({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            subscription_id: subscriptionId,
            subscription_name: subscriptionName || undefined,
            user_name: activeRole?.displayName || undefined,
            user_role: activeRole?.role || undefined
        });
        setSeedingPuzzles(true);
        setSeedingProgress({ current: 0, total: 725 }); // 725 PERFECT puzzles (300 + 425)
        let seedResult: { success: boolean; count: number } = { success: false, count: 0 };
        try {
            console.log('🌱 Seeding puzzles with:', { serviceProviderId, loggedInUserId, deleteOld });
            const result = await seedPuzzlesToDatabase(
                serviceProviderId, 
                loggedInUserId,
                (current, total) => {
                    setSeedingProgress({ current, total });
                },
                deleteOld // Delete old puzzles first to remove duplicates
            );
            seedResult = result;
            if (result.success) {
                // Reload puzzles after seeding
                await loadPuzzles();
                // Reload total count
                await loadTotalPuzzleCount();
            }
        } catch (error) {
            console.error('Error seeding puzzles:', error);
            toast.error('Failed to seed puzzles. Please try again.');
        } finally {
            setSeedingPuzzles(false);
            setSeedingProgress({ current: 0, total: 0 });
            trackChequeredSeedPuzzlesCompleted({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                subscription_id: subscriptionId,
                subscription_name: subscriptionName || undefined,
                user_name: activeRole?.displayName || undefined,
                user_role: activeRole?.role || undefined,
                count: seedResult.count,
                success: seedResult.success
            });
        }
    }, [serviceProviderId, loggedInUserId, loadPuzzles, loadTotalPuzzleCount, subscriptionId, subscriptionName, activeRole?.displayName, activeRole?.role]);
    
    const handleExcelImport = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !serviceProviderId || !loggedInUserId) {
            if (!file) toast.error('No file selected');
            return;
        }
        trackChequeredImportClicked({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            subscription_id: subscriptionId,
            subscription_name: subscriptionName || undefined,
            user_name: activeRole?.displayName || undefined,
            user_role: activeRole?.role || undefined,
            import_type: 'excel'
        });
        setImportingExcel(true);
        setExcelImportProgress({ current: 0, total: 100 });
        let importResult: { success: boolean; count: number } = { success: false, count: 0 };
        try {
            const result = await importPuzzlesFromExcel(
                file,
                serviceProviderId,
                loggedInUserId,
                (current, total) => {
                    setExcelImportProgress({ current, total });
                }
            );
            importResult = result;
            if (result.success) {
                await loadPuzzles();
                await loadTotalPuzzleCount();
            }
        } catch (error) {
            console.error('Error importing Excel:', error);
        } finally {
            setImportingExcel(false);
            setExcelImportProgress({ current: 0, total: 0 });
            trackChequeredImportCompleted({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                subscription_id: subscriptionId,
                subscription_name: subscriptionName || undefined,
                user_name: activeRole?.displayName || undefined,
                user_role: activeRole?.role || undefined,
                import_type: 'excel',
                count: importResult.count,
                success: importResult.success
            });
            event.target.value = '';
        }
    }, [serviceProviderId, loggedInUserId, loadPuzzles, loadTotalPuzzleCount, subscriptionId, subscriptionName, activeRole?.displayName, activeRole?.role]);
    
    // Load total count on mount and when puzzles change
    useEffect(() => {
        loadTotalPuzzleCount();
    }, [loadTotalPuzzleCount, puzzles.length]);
    
    // Load subscription puzzles
    const loadSubscriptionPuzzles = useCallback(async () => {
        if (!serviceProviderId || !activeSubscriptionForData || !isSubscriptionDataPage) return;
        
        setLoadingSubscriptionPuzzles(true);
        try {
            const puzzles = await getPuzzlesForSubscription(serviceProviderId, activeSubscriptionForData);
            setSubscriptionPuzzles(puzzles);
        } catch (error) {
            console.error('Error loading subscription puzzles:', error);
            toast.error('Failed to load subscription puzzles');
        } finally {
            setLoadingSubscriptionPuzzles(false);
        }
    }, [serviceProviderId, activeSubscriptionForData, isSubscriptionDataPage]);
    
    // Load which puzzles are already in subscription
    const loadPuzzlesInSubscription = useCallback(async () => {
        if (!serviceProviderId || !activeSubscriptionForData || !isSubscriptionDataPage) return;
        
        try {
            const puzzleIds = puzzles.map(p => p.id);
            const inSubscription = await getPuzzlesInSubscription(serviceProviderId, activeSubscriptionForData, puzzleIds);
            setPuzzlesInSubscription(inSubscription);
        } catch (error) {
            console.error('Error loading puzzles in subscription:', error);
        }
    }, [serviceProviderId, activeSubscriptionForData, puzzles, isSubscriptionDataPage]);
    
    useEffect(() => {
        if (subscriptionId) {
            setActiveSubscriptionForData(subscriptionId);
        }
    }, [subscriptionId]);

    // Load subscription name and data when on subscription data page
    useEffect(() => {
        const loadSubscriptionName = async () => {
            if (!serviceProviderId || !activeSubscriptionForData || !isSubscriptionDataPage) return;
            
            try {
                const subscriptionRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${activeSubscriptionForData}`);
                const subscriptionDoc = await getDoc(subscriptionRef);
                
                if (subscriptionDoc.exists()) {
                    const data = subscriptionDoc.data();
                    const name = data.Name || data.name || data.title || data.subscriptionName || data.planName || data.displayName || 'Class Puzzles';
                    setSubscriptionName(name);
                }
            } catch (error) {
                console.error('Error loading subscription name:', error);
            }
        };
        
        if (isSubscriptionDataPage) {
            loadSubscriptionName();
            loadSubscriptionPuzzles();
        }
    }, [isSubscriptionDataPage, serviceProviderId, activeSubscriptionForData, loadSubscriptionPuzzles]);

    // Analytics: Chequered page view
    useEffect(() => {
        if (!serviceProviderId || !loggedInUserId) return;
        const page = (['puzzles', 'teaching', 'neo', 'subscription-data', 'chess-basics', 'analysis', 'create', 'import'] as const).includes(currentPage as any)
            ? (currentPage as 'puzzles' | 'teaching' | 'neo' | 'subscription-data' | 'chess-basics' | 'analysis' | 'create' | 'import')
            : 'puzzles';
        trackChequeredPageViewed({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            subscription_id: subscriptionId,
            subscription_name: subscriptionName || undefined,
            user_name: activeRole?.displayName || undefined,
            user_role: activeRole?.role || undefined,
            page,
            is_enabled: !!chequeredEnabled
        });
    }, [serviceProviderId, loggedInUserId, currentPage, subscriptionId, subscriptionName, activeRole?.displayName, activeRole?.role, chequeredEnabled]);

    // Analytics: Class puzzles viewed (once when subscription puzzle list has loaded)
    const hasTrackedClassPuzzlesView = React.useRef(false);
    useEffect(() => {
        if (!isSubscriptionDataPage || !serviceProviderId || !loggedInUserId || !activeSubscriptionForData || loadingSubscriptionPuzzles) return;
        if (hasTrackedClassPuzzlesView.current) return;
        hasTrackedClassPuzzlesView.current = true;
        trackChequeredClassPuzzlesViewed({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            subscription_id: activeSubscriptionForData,
            subscription_name: subscriptionName || undefined,
            user_name: activeRole?.displayName || undefined,
            user_role: activeRole?.role || undefined
        });
    }, [isSubscriptionDataPage, serviceProviderId, loggedInUserId, activeSubscriptionForData, subscriptionName, loadingSubscriptionPuzzles, activeRole?.displayName, activeRole?.role]);
    useEffect(() => {
        if (!isSubscriptionDataPage) hasTrackedClassPuzzlesView.current = false;
    }, [isSubscriptionDataPage]);

    // Subscribe to active collaborative sessions
    useEffect(() => {
        if (subscriptionId && isSubscriptionDataPage) {
            unsubscribeActiveSessionsRef.current = getActiveSessions(subscriptionId, (sessions) => {
                setActiveSessions(sessions);
            });
        }

        return () => {
            if (unsubscribeActiveSessionsRef.current) {
                unsubscribeActiveSessionsRef.current();
            }
        };
    }, [subscriptionId, isSubscriptionDataPage]);

    // Handle joining via shared link (URL parameters)
    useEffect(() => {
        if (!subscriptionId || !isSubscriptionDataPage) return;
        
        const urlParams = new URLSearchParams(location.search);
        const collabSessionId = urlParams.get('collabSession');
        const linkSubscriptionId = urlParams.get('subscription');

        if (collabSessionId && linkSubscriptionId && linkSubscriptionId === subscriptionId && !showCollaborativeView) {
            // Check if session exists and join
            const unsubscribe = subscribeToSession(collabSessionId, subscriptionId, async (session) => {
                if (session && session.status === 'active' && session.puzzle) {
                    unsubscribe();
                    try {
                        // Determine if user is teacher (service provider or admin)
                        const userRole = (activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin') ? 'teacher' : 'student';
                        // Join the session
                        await joinCollaborativeSession(
                            collabSessionId,
                            subscriptionId,
                            loggedInUserId,
                            activeRole?.displayName || 'User',
                            userRole
                        );
                        // Set up the collaborative view
                        setCollaborativeSessionId(collabSessionId);
                        setCollaborativePuzzle(session.puzzle);
                        setShowCollaborativeView(true);
                        toast.success('Joined collaborative puzzle session!');
                        
                        // Clean up URL parameters
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    } catch (error) {
                        console.error('Error joining session from link:', error);
                        toast.error('Failed to join session. It may have ended.');
                        // Clean up URL parameters even on error
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    }
                } else if (session && session.status !== 'active') {
                    unsubscribe();
                    toast.error('This session has ended.');
                    // Clean up URL parameters
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);
                }
            });

            // Timeout after 5 seconds if session not found
            const timeout = setTimeout(() => {
                unsubscribe();
                toast.error('Session not found or expired.');
                // Clean up URL parameters
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            }, 5000);

            return () => {
                clearTimeout(timeout);
                unsubscribe();
            };
        }
    }, [location.search, subscriptionId, loggedInUserId, activeRole?.displayName, showCollaborativeView, isSubscriptionDataPage]);
    
    // Load which puzzles are in subscription when on puzzles page
    useEffect(() => {
        if (isPuzzlesPage && puzzles.length > 0 && activeSubscriptionForData) {
            loadPuzzlesInSubscription();
        }
    }, [isPuzzlesPage, puzzles.length, activeSubscriptionForData, loadPuzzlesInSubscription]);
    
    // Handle adding puzzle to subscription
    const handleAddToSubscription = useCallback(async (puzzleId: string) => {
        if (!serviceProviderId) return;
        
        // If chequeredEnabled is true and we have subscriptionId, use it directly
        // Otherwise, if chequeredEnabled is true but no subscriptionId, show selection dialog
        if (chequeredEnabled) {
            // Always ask which subscription to target; preselect current when available
            setPendingPuzzleId(puzzleId);
            if (subscriptionId) {
                setSelectedSubscriptionForPuzzle(subscriptionId);
            }
            setShowSubscriptionDialog(true);
        } else {
            // chequeredEnabled is false - use existing behavior (locked)
            if (!subscriptionId) return;
            
            const added = await addPuzzleToSubscription(
                serviceProviderId,
                subscriptionId,
                puzzleId,
                loggedInUserId
            );
            if (added) {
                trackChequeredPuzzleAddedToClass({
                    service_provider_id: serviceProviderId,
                    user_id: loggedInUserId,
                    subscription_id: subscriptionId,
                    subscription_name: subscriptionName || undefined,
                    user_name: activeRole?.displayName || undefined,
                    user_role: activeRole?.role || undefined,
                    puzzle_id: puzzleId
                });
                await loadPuzzlesInSubscription();
                if (isSubscriptionDataPage) {
                    await loadSubscriptionPuzzles();
                }
            }
        }
    }, [serviceProviderId, subscriptionId, loggedInUserId, isSubscriptionDataPage, loadPuzzlesInSubscription, loadSubscriptionPuzzles, chequeredEnabled, subscriptionName, activeRole?.displayName, activeRole?.role]);
    
    // Handle subscription selection and add puzzle
    const handleConfirmSubscriptionSelection = useCallback(async () => {
        if (!serviceProviderId || !selectedSubscriptionForPuzzle || !pendingPuzzleId) return;
        
        const added = await addPuzzleToSubscription(
            serviceProviderId,
            selectedSubscriptionForPuzzle,
            pendingPuzzleId,
            loggedInUserId
        );
        
        if (added) {
            trackChequeredPuzzleAddedToClass({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                subscription_id: selectedSubscriptionForPuzzle,
                subscription_name: subscriptionName || undefined,
                user_name: activeRole?.displayName || undefined,
                user_role: activeRole?.role || undefined,
                puzzle_id: pendingPuzzleId
            });
            toast.success('Puzzle added to subscription!');
            setShowSubscriptionDialog(false);
            setSelectedSubscriptionForPuzzle('');
            setPendingPuzzleId(null);
            if (isSubscriptionDataPage && subscriptionId === selectedSubscriptionForPuzzle) {
                await loadSubscriptionPuzzles();
            }
        }
    }, [serviceProviderId, selectedSubscriptionForPuzzle, pendingPuzzleId, loggedInUserId, isSubscriptionDataPage, subscriptionId, loadSubscriptionPuzzles, subscriptionName, activeRole?.displayName, activeRole?.role]);

    // Bulk selection handlers
    const toggleBulkSelection = useCallback(() => {
        setBulkSelectionMode(!bulkSelectionMode);
        if (bulkSelectionMode) {
            // Clear selections when exiting bulk mode
            setSelectedPuzzleIds(new Set());
        }
    }, [bulkSelectionMode]);

    const togglePuzzleSelection = useCallback((puzzleId: string) => {
        setSelectedPuzzleIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(puzzleId)) {
                newSet.delete(puzzleId);
            } else {
                newSet.add(puzzleId);
            }
            return newSet;
        });
    }, []);

    const selectAllFiltered = useCallback(() => {
        // Filter puzzles based on current filters
        const filtered = puzzles.filter(puzzle => {
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return (
                    puzzle.description?.toLowerCase().includes(query) ||
                    puzzle.themes?.some(theme => theme.toLowerCase().includes(query)) ||
                    puzzle.hint?.toLowerCase().includes(query)
                );
            }
            return true;
        });
        const allIds = new Set(filtered.map(p => p.id));
        setSelectedPuzzleIds(allIds);
    }, [puzzles, searchQuery]);

    const clearSelection = useCallback(() => {
        setSelectedPuzzleIds(new Set());
    }, []);

    const bulkAddToSubscription = useCallback(async () => {
        if (!serviceProviderId || selectedPuzzleIds.size === 0) return;

        // Show subscription selection dialog
        setShowSubscriptionDialog(true);
    }, [serviceProviderId, selectedPuzzleIds.size]);

    const handleBulkConfirmSubscriptionSelection = useCallback(async () => {
        if (!serviceProviderId || !selectedSubscriptionForPuzzle || selectedPuzzleIds.size === 0) return;

        setShowSubscriptionDialog(false);

        try {
            const puzzleIdsArray = Array.from(selectedPuzzleIds);
            const addedCount = await bulkAddPuzzlesToSubscription(
                serviceProviderId,
                selectedSubscriptionForPuzzle,
                puzzleIdsArray,
                loggedInUserId
            );

            if (addedCount > 0) {
                toast.success(`Added ${addedCount} puzzle${addedCount > 1 ? 's' : ''} to subscription!`);
                
                // Clear selections and exit bulk mode
                setSelectedPuzzleIds(new Set());
                setBulkSelectionMode(false);

                // Reload puzzles if we're on the subscription data page
                if (isSubscriptionDataPage && subscriptionId === selectedSubscriptionForPuzzle) {
                    await loadSubscriptionPuzzles();
                }
            }
        } catch (error) {
            console.error('Error bulk adding puzzles:', error);
            toast.error('Failed to add puzzles to subscription');
        }

        setSelectedSubscriptionForPuzzle('');
    }, [serviceProviderId, selectedSubscriptionForPuzzle, selectedPuzzleIds, loggedInUserId, isSubscriptionDataPage, subscriptionId, loadSubscriptionPuzzles]);

    // Render subscription selection dialog
    const renderSubscriptionDialog = () => (
        <Dialog 
            open={showSubscriptionDialog} 
            onClose={() => {
                setShowSubscriptionDialog(false);
                setSelectedSubscriptionForPuzzle('');
                setPendingPuzzleId(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 16,
                    padding: '6px 6px 12px',
                    background: 'linear-gradient(135deg, #fff, #f8f9ff)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>Choose a subscription</span>
                            <span style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: 999, background: '#EEF2FF', color: '#4F46E5', fontWeight: 700 }}>
                                Chess Puzzles
                            </span>
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: 4 }}>
                            Pick where this puzzle should live. We’ll show you that collection after adding.
                        </div>
                    </div>
                    <IconButton onClick={() => {
                        setShowSubscriptionDialog(false);
                        setSelectedSubscriptionForPuzzle('');
                        setPendingPuzzleId(null);
                    }}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <FormControl fullWidth sx={{ mt: 1.5 }}>
                    <InputLabel id="subscription-select-label">Subscription</InputLabel>
                    <Select
                        labelId="subscription-select-label"
                        value={selectedSubscriptionForPuzzle}
                        label="Subscription"
                        onChange={(e) => setSelectedSubscriptionForPuzzle(e.target.value)}
                        sx={{
                            borderRadius: 2,
                            background: '#fff',
                            boxShadow: '0 10px 30px rgba(79,70,229,0.08)'
                        }}
                    >
                        {subscriptionList.map((sub) => (
                            <MenuItem key={sub.id} value={sub.id}>
                                {sub.Name}
                            </MenuItem>
                        ))}
                    </Select>
                    <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 8 }}>
                        You can change this later; puzzles stay within the selected subscription’s collection.
                    </div>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
                <Button 
                    onClick={() => {
                        setShowSubscriptionDialog(false);
                        setSelectedSubscriptionForPuzzle('');
                        setPendingPuzzleId(null);
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={selectedPuzzleIds.size > 0 ? handleBulkConfirmSubscriptionSelection : handleConfirmSubscriptionSelection}
                    variant="contained"
                    disabled={!selectedSubscriptionForPuzzle}
                    sx={{ borderRadius: 999, px: 3 }}
                >
                    {selectedPuzzleIds.size > 0 ? `Add ${selectedPuzzleIds.size} puzzles` : 'Add to subscription'}
                </Button>
            </DialogActions>
        </Dialog>
    );
    
    // Show loading state while checking feature flag
    if (isLoadingFeatureFlag) {
        return (
            <>
                <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading Chequered Customer View...</p>
                    </div>
                </div>
                {renderSubscriptionDialog()}
            </>
        );
    }
    
    if (!subscriptionId) {
        return (
            <>
                <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Subscription Selected</h2>
                        <button
                            onClick={() => navigate('/mysubscriptions')}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                            Go to Subscriptions
                        </button>
                    </div>
                </div>
                {renderSubscriptionDialog()}
            </>
        );
    }

    if (!serviceProviderId) {
        return (
            <>
                <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading...</h2>
                    </div>
                </div>
                {renderSubscriptionDialog()}
            </>
        );
    }

    // Show collaborative puzzle view if active (check at top level before any page-specific rendering)
    const activeSubId = (activeSubscriptionForData && activeSubscriptionForData.trim()) || (subscriptionId && subscriptionId.trim()) || '';
    if (showCollaborativeView && collaborativeSessionId && collaborativePuzzle && activeSubId) {
        // Determine if user is teacher (service provider or admin)
        const userRole = (activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin' || isAdmin) ? 'teacher' : 'student';
        console.log('Rendering CollaborativePuzzleView', { 
            sessionId: collaborativeSessionId, 
            subscriptionId: activeSubId, 
            puzzleId: collaborativePuzzle.id,
            userRole 
        });
        return (
            <CollaborativePuzzleView
                sessionId={collaborativeSessionId}
                subscriptionId={activeSubId}
                puzzle={collaborativePuzzle}
                userId={loggedInUserId}
                userName={activeRole?.displayName || 'User'}
                userRole={userRole}
                onClose={() => {
                    setShowCollaborativeView(false);
                    setCollaborativeSessionId(null);
                    setCollaborativePuzzle(null);
                }}
            />
        );
    }
    
    // Debug log to see why collaborative view isn't showing
    if (showCollaborativeView) {
        console.log('Collaborative view should show but conditions not met:', {
            showCollaborativeView,
            collaborativeSessionId,
            collaborativePuzzle: !!collaborativePuzzle,
            activeSubId,
            activeSubscriptionForData,
            subscriptionId
        });
    }

    const filteredPuzzles = puzzles.filter(puzzle => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                puzzle.description?.toLowerCase().includes(query) ||
                puzzle.themes?.some(theme => theme.toLowerCase().includes(query)) ||
                puzzle.hint?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    // ========== TEACHING PAGE - ONLY BOARD ==========
    if (isTeachingPage) {
        return (
            <>
            <div
                className={`min-h-screen relative ${isNeoTeachingPage ? '' : 'bg-gray-50'}`}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    ...(isNeoTeachingPage ? { backgroundColor: '#262421' } : {})
                }}
            >
                {/* Mobile Warning */}
                {isMobile && showMobileWarning && (
                    <div className="bg-blue-50 border-b-2 border-blue-300 p-3 relative">
                        <button
                            onClick={() => setShowMobileWarning(false)}
                            className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                        <p className="text-blue-900 font-semibold text-xs sm:text-sm pr-6">
                            📱 Best on Tablet/Desktop - Chequered works best on larger screens
                        </p>
                    </div>
                )}
                
                {/* Locked Overlay */}
                {!chequeredEnabled && (
                    <div className="absolute inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
                        <div className="max-w-lg">
                            <ChequeredPromotion />
                        </div>
                    </div>
                )}
                
                {/* Header */}
                <div
                    style={{
                        background: isNeoTeachingPage ? '#302e2b' : 'white',
                        padding: '0.75rem 1.5rem',
                        borderBottom: isNeoTeachingPage ? '1px solid #3d3a36' : '1px solid #e5e7eb',
                        boxShadow: isNeoTeachingPage ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1920px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                            onClick={() => navigate(`/chequered/${subscriptionId}`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: isNeoTeachingPage ? '#bababa' : '#6b7280',
                                    padding: '0.375rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Back to CheQuered Home"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                            </button>
                            <div>
                                <h1 style={{ fontSize: '1.25rem', fontWeight: 600, color: isNeoTeachingPage ? '#bababa' : '#111827', margin: 0 }}>
                                    {isNeoTeachingPage ? 'Board editor' : '📚 Teaching Board'} {!chequeredEnabled && '🔒'}
                                </h1>
                                {isNeoTeachingPage && (
                                    <p style={{ fontSize: '0.75rem', color: '#8c8b88', margin: '0.25rem 0 0 0' }}>
                                        Lichess-style setup (coordinates, spare pieces, FEN &amp; URL)
                                    </p>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {selectedPresetName && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.375rem 0.75rem',
                                    background: '#ede9fe',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    color: '#6b21a8'
                                }}>
                                    <span style={{ fontWeight: 500 }}>📋 {selectedPresetName}</span>
                                    <button
                                        onClick={() => {
                                            setSelectedPresetPosition(null);
                                            setSelectedPresetName(null);
                                            toast.info('Preset cleared');
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#6b21a8',
                                            padding: '0.125rem 0.25rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: '0.25rem'
                                        }}
                                        title="Clear preset"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 6L6 18M6 6l12 12"/>
                                        </svg>
                                    </button>
                                </div>
                            )}
                            <button
                                onClick={() => setShowPresetModal(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    background: '#9333ea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#7e22ce';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#9333ea';
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"/>
                                </svg>
                                Load Preset
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Board - Full Width */}
                <div style={{ 
                    flex: 1, 
                    overflow: 'hidden',
                    filter: !chequeredEnabled ? 'blur(8px)' : 'none', 
                    pointerEvents: !chequeredEnabled ? 'none' : 'auto' 
                }}>
                    <ChequeredChess
                        subscriptionId={subscriptionId}
                        subscriptionName="Chess Practice"
                        userRole={activeRole?.role as 'teacher' | 'student' | undefined}
                        isInMeeting={isInMeeting}
                        participantName={activeRole?.displayName || 'Player'}
                        room={room}
                        disableTeachingMode={false}
                        initialPuzzle={null}
                        initialMode={'teaching'}
                        initialPosition={selectedPresetPosition}
                        teachingBoardVariant={isNeoTeachingPage ? 'neo' : 'standard'}
                    />
                </div>
                
                {/* Preset Selection Modal */}
                <Dialog 
                    open={showPresetModal} 
                    onClose={() => {
                        setShowPresetModal(false);
                        setPresetSearchQuery('');
                    }}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '0.75rem',
                            maxHeight: '80vh'
                        }
                    }}
                >
                    <DialogTitle style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '1.25rem 1.5rem',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <div>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                                📚 Load Preset Position
                            </h2>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                                Search and select a chess opening or pattern
                            </p>
                        </div>
                        <IconButton
                            onClick={() => {
                                setShowPresetModal(false);
                                setPresetSearchQuery('');
                            }}
                            size="small"
                            style={{ color: '#6b7280' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ padding: '1.5rem' }}>
                        {/* Search Input */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{
                                    position: 'absolute',
                                    left: '0.75rem',
                                    color: '#9ca3af'
                                }}>
                                    <path d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"/>
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search presets by name or description..."
                                    value={presetSearchQuery}
                                    onChange={(e) => setPresetSearchQuery(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 0.75rem 0.75rem 2.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.875rem',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#9333ea';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                    }}
                                />
                            </div>
                        </div>
                        
                        {/* Preset List */}
                        <div style={{
                            maxHeight: '50vh',
                            overflowY: 'auto',
                            paddingRight: '0.5rem'
                        }}>
                            {(() => {
                                // Filter presets based on search query
                                const filteredPresets = TEACHING_PRESET_POSITIONS.filter(preset => {
                                    if (!presetSearchQuery.trim()) return true;
                                    const query = presetSearchQuery.toLowerCase();
                                    return preset.name.toLowerCase().includes(query) ||
                                           preset.description.toLowerCase().includes(query) ||
                                           preset.category.toLowerCase().includes(query);
                                });
                                
                                // Group by category
                                const groupedPresets = filteredPresets.reduce((acc, preset) => {
                                    if (!acc[preset.category]) {
                                        acc[preset.category] = [];
                                    }
                                    acc[preset.category].push(preset);
                                    return acc;
                                }, {} as Record<string, typeof TEACHING_PRESET_POSITIONS>);
                                
                                if (filteredPresets.length === 0) {
                                    return (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '3rem 1rem',
                                            color: '#6b7280'
                                        }}>
                                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
                                            <p style={{ fontSize: '0.875rem', margin: 0 }}>
                                                No presets found matching "{presetSearchQuery}"
                                            </p>
                                        </div>
                                    );
                                }
                                
                                return Object.entries(groupedPresets).map(([category, presets]) => (
                                    <div key={category} style={{ marginBottom: '1.5rem' }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            marginBottom: '0.75rem',
                                            paddingLeft: '0.5rem'
                                        }}>
                                            {category === 'opening' && 'Openings'}
                                            {category === 'pattern' && 'Patterns'}
                                            {category === 'tactical' && 'Tactical'}
                                            {category === 'endgame' && 'Endgames'}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {presets.map(preset => (
                                                <button
                                                    key={preset.id}
                                                    onClick={() => {
                                                        setSelectedPresetPosition(preset.fen);
                                                        setSelectedPresetName(preset.name);
                                                        setShowPresetModal(false);
                                                        setPresetSearchQuery('');
                                                        toast.success(`Loaded: ${preset.name}`);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.875rem 1rem',
                                                        background: selectedPresetPosition === preset.fen ? '#ede9fe' : 'white',
                                                        border: selectedPresetPosition === preset.fen ? '2px solid #9333ea' : '1px solid #e5e7eb',
                                                        borderRadius: '0.5rem',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        boxShadow: selectedPresetPosition === preset.fen ? '0 4px 6px -1px rgba(147, 51, 234, 0.1)' : 'none'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (selectedPresetPosition !== preset.fen) {
                                                            e.currentTarget.style.background = '#f9fafb';
                                                            e.currentTarget.style.borderColor = '#d1d5db';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedPresetPosition !== preset.fen) {
                                                            e.currentTarget.style.background = 'white';
                                                            e.currentTarget.style.borderColor = '#e5e7eb';
                                                        }
                                                    }}
                                                >
                                                    <div style={{
                                                        fontSize: '0.9375rem',
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {preset.name}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#6b7280',
                                                        lineHeight: '1.4'
                                                    }}>
                                                        {preset.description}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    </DialogContent>
                    <DialogActions style={{ 
                        padding: '1rem 1.5rem',
                        borderTop: '1px solid #e5e7eb',
                        justifyContent: 'space-between'
                    }}>
                        {selectedPresetPosition && (
                            <Button
                                onClick={() => {
                                    setSelectedPresetPosition(null);
                                    setSelectedPresetName(null);
                                    toast.info('Preset cleared');
                                }}
                                style={{
                                    color: '#6b7280',
                                    textTransform: 'none'
                                }}
                            >
                                Clear Selection
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                setShowPresetModal(false);
                                setPresetSearchQuery('');
                            }}
                            variant="contained"
                            style={{
                                background: '#9333ea',
                                color: 'white',
                                textTransform: 'none',
                                marginLeft: 'auto'
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
            {renderSubscriptionDialog()}
        </>
        );
    }

    // ========== ANALYSIS PAGE - ONLY ANALYSIS ==========
    if (isAnalysisPage) {
        return (
            <>
            <div className="min-h-screen bg-gray-50" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={{
                    background: 'white',
                    padding: '1rem 2rem',
                    borderBottom: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1920px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                            onClick={() => navigate(`/chequered/${subscriptionId}`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Back to CheQuered Home"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                            </button>
                            <div>
                                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                    🔍 Game Analysis
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Analysis View - Full Screen */}
                <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
                    <AnalyzeGameView 
                        serviceProviderId={serviceProviderId}
                        loggedInUserId={loggedInUserId}
                    />
                </div>
            </div>
            {renderSubscriptionDialog()}
        </>
        );
    }

    // ========== SUBSCRIPTION DATA PAGE - ONLY SUBSCRIPTION PUZZLES ==========
    if (isSubscriptionDataPage) {
        return (
            <>
            <div className="bg-gray-50" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{
                    background: 'white',
                    padding: '0.75rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1920px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                            onClick={() => navigate(`/chequered/${subscriptionId}`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Back to CheQuered Home"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                            </button>
                            <div>
                                <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                    📊 Class Puzzles Data
                                    {subscriptionName && (
                                        <span style={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', marginLeft: '0.5rem' }}>
                                            - {subscriptionName}
                                        </span>
                                    )}
                                </h1>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                                    Puzzle Store items in this class ({subscriptionPuzzles.length} total)
                                </p>
                            </div>
                        </div>
                        {subscriptionList.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Subscription
                                </div>
                                <select
                                    value={activeSubscriptionForData}
                                    onChange={(e) => {
                                        setActiveSubscriptionForData(e.target.value);
                                        setSelectedPuzzle(null);
                                    }}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '0.65rem',
                                        border: '1px solid #e5e7eb',
                                        background: '#f9fafb',
                                        color: '#111827',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        minWidth: '220px',
                                        boxShadow: '0 10px 30px rgba(79,70,229,0.08)'
                                    }}
                                >
                                    {subscriptionList.map((sub) => (
                                        <option key={sub.id} value={sub.id}>
                                            {sub.Name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Subscription Puzzles - Grid View */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                    {loadingSubscriptionPuzzles ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                            <p style={{ fontSize: '0.875rem' }}>Loading class puzzles...</p>
                        </div>
                    ) : subscriptionPuzzles.length === 0 ? (
                        <div className="empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                            <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No puzzles added to this class yet.</p>
                            <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>Go to Puzzle Store to add puzzles from the main library.</p>
                            <button
                                onClick={() => navigate(`/chequered/${subscriptionId}/puzzles`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#9333ea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Open Puzzle Store
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Filter Bar */}
                            <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.875rem', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 600, color: '#111827' }}>
                                            Total: {subscriptionPuzzles.length}
                                        </span>
                                        <span style={{ color: '#10b981' }}>
                                            ✓ Solved: {subscriptionPuzzles.filter(p => (p as any).isSolved).length}
                                        </span>
                                        <span style={{ color: '#3b82f6' }}>
                                            📚 Taught: {subscriptionPuzzles.filter(p => (p as any).isTaught).length}
                                        </span>
                                        <span style={{ color: '#f59e0b' }}>
                                            ⏳ Pending: {subscriptionPuzzles.filter(p => !(p as any).isSolved && !(p as any).isTaught).length}
                                        </span>
                                        <button
                                            onClick={() => setShowClassPuzzlesGuide(true)}
                                            style={{
                                                padding: '0.375rem 0.75rem',
                                                background: '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.375rem'
                                            }}
                                            title="Show help guide"
                                        >
                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Help
                                        </button>
                                    </div>
                                    {isAdmin && subscriptionPuzzles.length > 0 && (
                                        <button
                                            onClick={async () => {
                                                if (window.confirm(`Delete all ${subscriptionPuzzles.length} puzzles from this class? This cannot be undone.`)) {
                                                    // Delete all puzzles from subscription
                                                    for (const puzzle of subscriptionPuzzles) {
                                                        await removePuzzleFromSubscription(
                                                            serviceProviderId,
                                                            activeSubscriptionForData || subscriptionId!,
                                                            puzzle.id
                                                        );
                                                    }
                                                    await loadSubscriptionPuzzles();
                                                    toast.success('All puzzles removed from class');
                                                }
                                            }}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete All
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {(['all', 'unsolved', 'solved', 'taught'] as const).map((filter) => (
                                        <button
                                            key={filter}
                                            onClick={() => setPuzzleFilter(filter)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: puzzleFilter === filter ? '#9333ea' : '#f3f4f6',
                                                color: puzzleFilter === filter ? 'white' : '#6b7280',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {filter === 'all' ? 'All Puzzles' : 
                                             filter === 'unsolved' ? 'Unsolved' : 
                                             filter === 'solved' ? 'Solved' : 'Taught'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Puzzle Grid */}
                            <div style={{ 
                                flex: 1, 
                                overflowY: 'auto', 
                                padding: '1.5rem',
                                background: '#f9fafb'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                    gap: '0.75rem'
                                }}>
                                    {subscriptionPuzzles
                                        .filter(puzzle => {
                                            const puzzleData = puzzle as any;
                                            if (puzzleFilter === 'all') return true;
                                            if (puzzleFilter === 'solved') return puzzleData.isSolved;
                                            if (puzzleFilter === 'taught') return puzzleData.isTaught;
                                            if (puzzleFilter === 'unsolved') return !puzzleData.isSolved && !puzzleData.isTaught;
                                            return true;
                                        })
                                        .map((puzzle) => {
                                            const puzzleData = puzzle as any;
                                            const isSolved = puzzleData.isSolved || false;
                                            const isTaught = puzzleData.isTaught || false;
                                            
                                            return (
                                                <motion.div
                                                    key={puzzle.id}
                                                    whileHover={{ scale: 1.02, y: -4 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => handlePuzzleSelect(puzzle)}
                                                    style={{
                                                        padding: '0.75rem',
                                                        borderRadius: '0.5rem',
                                                        background: 'white',
                                                        border: `2px solid ${isSolved ? '#10b981' : isTaught ? '#3b82f6' : '#e5e7eb'}`,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        position: 'relative',
                                                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                                                    }}
                                                >
                                                    {/* Status Badge */}
                                                    {(isSolved || isTaught) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '0.5rem',
                                                            right: '0.5rem',
                                                            padding: '0.125rem 0.375rem',
                                                            background: isSolved ? '#10b981' : '#3b82f6',
                                                            color: 'white',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.65rem',
                                                            fontWeight: 600
                                                        }}>
                                                            {isSolved ? '✓ Solved' : '📚 Taught'}
                                                        </div>
                                                    )}

                                                    {/* Puzzle Info */}
                                                    <div style={{ paddingRight: isSolved || isTaught ? '3.5rem' : '0' }}>
                                                        <h3 style={{ 
                                                            fontSize: '0.875rem', 
                                                            fontWeight: 600, 
                                                            color: '#111827', 
                                                            marginBottom: '0.5rem',
                                                            lineHeight: '1.3',
                                                            wordWrap: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {puzzle.description || `Puzzle #${puzzle.id.slice(7, 13)}`}
                                                        </h3>

                                                        {/* Complexity & Rating */}
                                                        <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                                            {puzzle.complexity && (
                                                                <span style={{
                                                                    padding: '0.125rem 0.375rem',
                                                                    borderRadius: '0.25rem',
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 600,
                                                                    ...(puzzle.complexity === 'beginner' ? { background: '#dcfce7', color: '#166534' } :
                                                                        puzzle.complexity === 'intermediate' ? { background: '#fef3c7', color: '#92400e' } :
                                                                        { background: '#fee2e2', color: '#991b1b' })
                                                                }}>
                                                                    {puzzle.complexity.toUpperCase()}
                                                                </span>
                                                            )}
                                                            <span style={{
                                                                padding: '0.125rem 0.375rem',
                                                                borderRadius: '0.25rem',
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                                background: '#f3f4f6',
                                                                color: '#374151'
                                                            }}>
                                                                ⭐ {puzzle.rating}
                                                            </span>
                                                            <span style={{
                                                                padding: '0.125rem 0.375rem',
                                                                borderRadius: '0.25rem',
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                                background: '#ede9fe',
                                                                color: '#7c3aed'
                                                            }}>
                                                                {puzzle.category}
                                                            </span>
                                                        </div>

                                                        {/* Hint Preview */}
                                                        {puzzle.hint && (
                                                            <p style={{
                                                                fontSize: '0.7rem',
                                                                color: '#6b7280',
                                                                marginBottom: '0.5rem',
                                                                lineHeight: '1.3',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}>
                                                                💡 {puzzle.hint}
                                                            </p>
                                                        )}

                                                        {/* Action Buttons */}
                                                        <div style={{
                                                            marginTop: '0.5rem',
                                                            paddingTop: '0.5rem',
                                                            borderTop: '1px solid #e5e7eb',
                                                            display: 'flex',
                                                            gap: '0.375rem',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try {
                                                                        // Determine if user is teacher (service provider or admin)
                                                                        const userRole = (activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin' || isAdmin) ? 'teacher' : 'student';
                                                                        const subId = activeSubscriptionForData || subscriptionId;
                                                                        if (!subId) {
                                                                            toast.error('No subscription selected');
                                                                            return;
                                                                        }
                                                                        if (!loggedInUserId) {
                                                                            toast.error('User not logged in');
                                                                            return;
                                                                        }
                                                                        console.log('Creating collaborative session...', { puzzleId: puzzle.id, subId, userId: loggedInUserId, userRole });
                                                                        let sessionId: string;
                                                                        try {
                                                                            sessionId = await createCollaborativeSession(
                                                                                puzzle,
                                                                                subId,
                                                                                loggedInUserId,
                                                                                activeRole?.displayName || 'User',
                                                                                userRole
                                                                            );
                                                                            console.log('Session created successfully:', sessionId);
                                                                            trackChequeredCollabSessionStarted({
                                                                                service_provider_id: serviceProviderId,
                                                                                user_id: loggedInUserId,
                                                                                subscription_id: subId,
                                                                                subscription_name: subscriptionName || undefined,
                                                                                user_name: activeRole?.displayName || undefined,
                                                                                user_role: activeRole?.role || undefined,
                                                                                session_id: sessionId,
                                                                                puzzle_id: puzzle.id
                                                                            });
                                                                        } catch (createError) {
                                                                            console.error('Error in createCollaborativeSession:', createError);
                                                                            throw createError; // Re-throw to be caught by outer catch
                                                                        }
                                                                        toast.success('Collaborative session started! Redirecting...');
                                                                        navigate(`/chequered/collaborative/${sessionId}?role=teacher`);
                                                                    } catch (error) {
                                                                        console.error('Error creating collaborative session:', error);
                                                                        toast.error('Failed to start collaborative session: ' + (error instanceof Error ? error.message : String(error)));
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem 0.5rem',
                                                                    background: 'linear-gradient(to right, #9333ea, #3b82f6)',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '0.25rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '0.25rem',
                                                                    flex: '1 1 auto',
                                                                    minWidth: '120px',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 600
                                                                }}
                                                                title="Start collaborative solving session"
                                                            >
                                                                <span>🤝</span>
                                                                <span>Collaborative Solve</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedPuzzle(puzzle);
                                                                    setShowHint(false); // Reset hint visibility when opening modal
                                                                    setShowPuzzleModal(true);
                                                                    if (serviceProviderId && loggedInUserId) {
                                                                        trackChequeredPuzzleOpened({
                                                                            service_provider_id: serviceProviderId,
                                                                            user_id: loggedInUserId,
                                                                            subscription_id: activeSubscriptionForData || subscriptionId,
                                                                            subscription_name: subscriptionName || undefined,
                                                                            user_name: activeRole?.displayName || undefined,
                                                                            user_role: activeRole?.role || undefined,
                                                                            puzzle_id: puzzle.id,
                                                                            puzzle_rating: puzzle.rating,
                                                                            puzzle_complexity: puzzle.complexity,
                                                                            puzzle_category: puzzle.category,
                                                                            source: 'list'
                                                                        });
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: '#9333ea',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '0.25rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flex: 1
                                                                }}
                                                                title="Solve puzzle"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                                                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                                                </svg>
                                                            </button>
                                                            {!isSolved && (
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        const marked = await markPuzzleAsSolved(
                                                                            serviceProviderId,
                                                                            activeSubscriptionForData || subscriptionId!,
                                                                            puzzle.id,
                                                                            loggedInUserId,
                                                                            activeRole?.displayName || 'User'
                                                                        );
                                                                        if (marked) {
                                                                            trackChequeredPuzzleMarkedSolved({
                                                                                service_provider_id: serviceProviderId,
                                                                                user_id: loggedInUserId,
                                                                                subscription_id: activeSubscriptionForData || subscriptionId,
                                                                                subscription_name: subscriptionName || undefined,
                                                                                user_name: activeRole?.displayName || undefined,
                                                                                user_role: activeRole?.role || undefined,
                                                                                puzzle_id: puzzle.id,
                                                                                context: 'class_list'
                                                                            });
                                                                            await loadSubscriptionPuzzles();
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        padding: '0.375rem',
                                                                        background: '#10b981',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '0.25rem',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flex: 1
                                                                    }}
                                                                    title="Mark as solved"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (window.confirm('Remove this puzzle from the class?')) {
                                                                        const removed = await removePuzzleFromSubscription(
                                                                            serviceProviderId,
                                                                            activeSubscriptionForData || subscriptionId!,
                                                                            puzzle.id
                                                                        );
                                                                        if (removed) {
                                                                            trackChequeredPuzzleRemovedFromClass({
                                                                                service_provider_id: serviceProviderId,
                                                                                user_id: loggedInUserId,
                                                                                subscription_id: activeSubscriptionForData || subscriptionId,
                                                                                subscription_name: subscriptionName || undefined,
                                                                                user_name: activeRole?.displayName || undefined,
                                                                                user_role: activeRole?.role || undefined,
                                                                                puzzle_id: puzzle.id
                                                                            });
                                                                            await loadSubscriptionPuzzles();
                                                                        }
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: '#ef4444',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '0.25rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                title="Remove from class"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Old List View - Remove everything below */}
                {false && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>
                                        Puzzle Store ({subscriptionPuzzles.length})
                                    </h3>
                                    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                        <span>✓ Solved: {subscriptionPuzzles.filter(p => (p as any).isSolved).length}</span>
                                        <span>📚 Taught: {subscriptionPuzzles.filter(p => (p as any).isTaught).length}</span>
                                    </div>
                                </div>
                                {subscriptionPuzzles.map((puzzle) => {
                                    const puzzleData = puzzle as any;
                                    const isSolved = puzzleData.isSolved || false;
                                    const isTaught = puzzleData.isTaught || false;
                                    
                                    return (
                                        <div
                                            key={puzzle.id}
                                            onClick={() => handlePuzzleSelect(puzzle)}
                                            style={{
                                                padding: '1rem',
                                                borderRadius: '0.5rem',
                                                border: `2px solid ${selectedPuzzle?.id === puzzle.id ? '#9333ea' : isSolved ? '#10b981' : isTaught ? '#3b82f6' : '#e5e7eb'}`,
                                                background: selectedPuzzle?.id === puzzle.id ? '#f3e8ff' : isSolved ? '#f0fdf4' : isTaught ? '#eff6ff' : 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                position: 'relative'
                                            }}
                                        >
                                            {isSolved && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '0.5rem',
                                                    right: '0.5rem',
                                                    padding: '0.25rem 0.5rem',
                                                    background: '#10b981',
                                                    color: 'white',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    ✓ Solved
                                                </div>
                                            )}
                                            {isTaught && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: isSolved ? '2rem' : '0.5rem',
                                                    right: '0.5rem',
                                                    padding: '0.25rem 0.5rem',
                                                    background: '#3b82f6',
                                                    color: 'white',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    📚 Taught
                                                </div>
                                            )}
                                            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem', paddingRight: '4rem' }}>
                                                {puzzle.description || `Puzzle #${puzzle.id.slice(0, 6)}`}
                                            </h3>
                                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                                {puzzle.complexity && (
                                                    <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '0.25rem',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        ...(puzzle.complexity === 'beginner' ? { background: '#dcfce7', color: '#166534' } :
                                                            puzzle.complexity === 'intermediate' ? { background: '#fef3c7', color: '#92400e' } :
                                                            { background: '#fee2e2', color: '#991b1b' })
                                                    }}>
                                                        {puzzle.complexity}
                                                    </span>
                                                )}
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    Rating: {puzzle.rating}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                                <button
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        try {
                                                            // Determine if user is teacher (service provider or admin)
                                                            const userRole = (activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin' || isAdmin) ? 'teacher' : 'student';
                                                            const sessionId = await createCollaborativeSession(
                                                                puzzle,
                                                                activeSubscriptionForData || subscriptionId!,
                                                                loggedInUserId,
                                                                activeRole?.displayName || 'User',
                                                                userRole
                                                            );
                                                            // Redirect teacher to collaborative session page
                                                            toast.success('Collaborative session started! Redirecting...');
                                                            navigate(`/chequered/collaborative/${sessionId}?role=teacher`);
                                                        } catch (error) {
                                                            console.error('Error creating collaborative session:', error);
                                                            toast.error('Failed to start collaborative session');
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '0.375rem 0.75rem',
                                                        background: 'linear-gradient(to right, #9333ea, #3b82f6)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        flex: '1 1 auto',
                                                        minWidth: '140px'
                                                    }}
                                                >
                                                    🤝 Collaborative Solve
                                                </button>
                                                {!isTaught && (
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const marked = await markPuzzleAsTaught(
                                                                serviceProviderId,
                                                                activeSubscriptionForData || subscriptionId!,
                                                                puzzle.id,
                                                                loggedInUserId,
                                                                activeRole?.displayName || 'Teacher'
                                                            );
                                                            if (marked) {
                                                                await loadSubscriptionPuzzles();
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            background: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Mark as Taught
                                                    </button>
                                                )}
                                                {isTaught && (
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const unmarked = await unmarkPuzzleAsTaught(
                                                                serviceProviderId,
                                                                activeSubscriptionForData || subscriptionId!,
                                                                puzzle.id
                                                            );
                                                            if (unmarked) {
                                                                await loadSubscriptionPuzzles();
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            background: '#6b7280',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Unmark Taught
                                                    </button>
                                                )}
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        const removed = await removePuzzleFromSubscription(
                                                            serviceProviderId,
                                                            activeSubscriptionForData || subscriptionId!,
                                                            puzzle.id
                                                        );
                                                        if (removed) {
                                                            trackChequeredPuzzleRemovedFromClass({
                                                                service_provider_id: serviceProviderId,
                                                                user_id: loggedInUserId,
                                                                subscription_id: activeSubscriptionForData || subscriptionId,
                                                                subscription_name: subscriptionName || undefined,
                                                                user_name: activeRole?.displayName || undefined,
                                                                user_role: activeRole?.role || undefined,
                                                                puzzle_id: puzzle.id
                                                            });
                                                            await loadSubscriptionPuzzles();
                                                            if (selectedPuzzle?.id === puzzle.id) {
                                                                setSelectedPuzzle(null);
                                                            }
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '0.25rem',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
            </div>
            {renderSubscriptionDialog()}
        </>
        );
    }

    // ========== CHESS BASICS PAGE - CHESSPEDIA ==========
    if (isChessBasicsPage) {
        return (
            <>
            <div className="min-h-screen bg-gray-50" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={{
                    background: 'white',
                    padding: '1rem 2rem',
                    borderBottom: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1920px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                                onClick={() => navigate(`/chequered/${subscriptionId}`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Back to CheQuered Home"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                            </button>
                            <div>
                                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                    📖 Chess Basics & Chesspedia
                                </h1>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                                    Educational content and chess knowledge base
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Chess Basics Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
                    <ChessBasicsView 
                        serviceProviderId={serviceProviderId}
                        loggedInUserId={loggedInUserId}
                        userName={activeRole?.displayName || 'User'}
                        activeRole={activeRole}
                    />
                </div>
            </div>
            {renderSubscriptionDialog()}
        </>
        );
    }

    // ========== PUZZLES PAGE - ONLY PUZZLES WITH CATEGORIES ==========
    return (
        <>
        <div className="min-h-screen bg-gray-50 relative">
            {/* Mobile Warning */}
            {isMobile && showMobileWarning && (
                <div className="bg-blue-50 border-b-2 border-blue-300 p-3 relative">
                    <button
                        onClick={() => setShowMobileWarning(false)}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </button>
                    <p className="text-blue-900 font-semibold text-xs sm:text-sm pr-6">
                        📱 Best on Tablet/Desktop - Chequered works best on larger screens
                    </p>
                </div>
            )}
            
            {/* Locked Overlay */}
            {!chequeredEnabled && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
                    <div className="max-w-lg">
                        <ChequeredPromotion />
                    </div>
                </div>
            )}
            
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="training-header"
                style={{
                    background: 'white',
                    padding: '2rem',
                    borderBottom: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    filter: !chequeredEnabled ? 'blur(8px)' : 'none'
                }}
            >
                <div style={{ maxWidth: '1920px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                            <button
                                onClick={() => navigate(`/chequered/${subscriptionId}`, { replace: true, state: { from: fromPath } })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Back to CheQuered Home"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                            </button>
                            <div>
                                <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
                                    ♟️ Puzzle Store
                                </h1>
                                <p style={{ fontSize: '1rem', color: '#6b7280', margin: 0 }}>
                                    Browse and manage puzzle store content for your classes
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <button
                                onClick={handleSeedPuzzles}
                                disabled={seedingPuzzles}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: seedingPuzzles ? '#9ca3af' : '#9333ea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: seedingPuzzles ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                {seedingPuzzles ? '🌱 Seeding...' : '🌱 Seed Puzzles'}
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Main Content - CheQuered Quest Style Layout */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="training-container"
                style={{
                    maxWidth: '1920px',
                    margin: '0 auto',
                    padding: '2rem',
                    display: 'flex',
                    gap: '2rem',
                    height: 'calc(100vh - 200px)'
                }}
            >
                {/* Feature Showcase removed - no longer needed */}

                {/* Left Sidebar - Module Selector (CheQuered Quest Style) - Hidden on Mobile */}
                <motion.aside
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                    className="training-sidebar hidden md:flex"
                    style={{
                        width: '280px',
                        flexShrink: 0,
                        background: 'white',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        height: '100%',
                        overflow: 'hidden'
                    }}
                >
                    {/* Scrollable Content */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Module Selector */}
                        <div className="module-selector" style={{ 
                            padding: '1rem', 
                            borderBottom: '1px solid #e5e7eb', 
                            flexShrink: 0
                        }}>
                                    {categories.map((category) => (
                                        <motion.button
                                            key={category.id}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                            onClick={() => {
                                                setActiveCategory(category.id);
                                                setSelectedPuzzle(null);
                                                setSearchQuery(''); // Clear search when changing category
                                                if (serviceProviderId && loggedInUserId) {
                                                    trackChequeredCategorySelected({
                                                        service_provider_id: serviceProviderId,
                                                        user_id: loggedInUserId,
                                                        subscription_id: subscriptionId,
                                                        subscription_name: subscriptionName || undefined,
                                                        user_name: activeRole?.displayName || undefined,
                                                        user_role: activeRole?.role || undefined,
                                                        category: category.id,
                                                        category_label: category.label
                                                    });
                                                }
                                            }}
                                    className={`module-btn ${activeCategory === category.id ? 'active' : ''}`}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem',
                                        marginBottom: '0.5rem',
                                        background: activeCategory === category.id ? '#f3e8ff' : 'transparent',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        textAlign: 'left'
                                    }}
                                >
                                    <span className="module-icon" style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.125rem' }}>
                                            <div className="module-label" style={{ 
                                                fontSize: '0.875rem', 
                                                fontWeight: 600, 
                                                color: activeCategory === category.id ? '#7c3aed' : '#374151'
                                            }}>
                                                {category.label}
                                            </div>
                                            {puzzleCountsByCategory[category.id] !== undefined && (
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: activeCategory === category.id ? '#7c3aed' : '#9ca3af',
                                                    background: activeCategory === category.id ? '#ede9fe' : '#f3f4f6',
                                                    padding: '0.125rem 0.5rem',
                                                    borderRadius: '0.375rem'
                                                }}>
                                                    {puzzleCountsByCategory[category.id]}
                                                </span>
                                            )}
                                        </div>
                                        <div className="module-desc" style={{ 
                                            fontSize: '0.75rem', 
                                            color: '#6b7280' 
                                        }}>
                                            {category.description}
                                        </div>
                                    </div>
                                        </motion.button>
                                    ))}
                            </div>

                        {/* Import/Create Section - Buttons to Open Dialogs */}
                        <div className="sidebar-actions" style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button
                                onClick={() => setShowCreatePuzzleModal(true)}
                                disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    background: 'linear-gradient(to right, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <span>✨</span>
                                <span>Create Puzzle</span>
                            </button>
                            {SHOW_IMPORT_PUZZLES && (
                            <button
                                onClick={() => setShowImportDialog(true)}
                                disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    background: 'linear-gradient(to right, #9333ea, #3b82f6)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <span>📥</span>
                                <span>Import Puzzles</span>
                            </button>
                            )}
                        </div>

                        {/* Category Info */}
                        <div className="puzzle-list" style={{ flexShrink: 0, padding: '1rem' }}>
                            {activeCategory && categories.find(c => c.id === activeCategory) && (
                                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                                    <p style={{ fontSize: '0.875rem' }}>
                                        {categories.find(c => c.id === activeCategory)?.description}
                                    </p>
                                    {puzzleCountsByCategory[activeCategory] !== undefined && (
                                        <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                            {puzzleCountsByCategory[activeCategory] || 0} puzzles
                                        </p>
                                    )}
                                    </div>
                            )}
                        </div>
                    </div>
                </motion.aside>

                {/* Main Content - ONLY PUZZLES (no teaching board, no analysis) */}
                <motion.main
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                    className="training-main"
                    style={{
                        flex: 1,
                        background: 'white',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {SHOW_IMPORT_PUZZLES && activeCategory === 'import' ? (
                            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ 
                                    padding: '1rem', 
                                    background: 'linear-gradient(to right, #dcfce7, #bbf7d0)', 
                                    borderRadius: '0.5rem',
                                    border: '2px solid #86efac'
                                }}>
                                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#166534', marginBottom: '0.5rem' }}>
                                        📊 CSV Bulk Import (Recommended)
                                    </p>
                                    <p style={{ fontSize: '0.75rem', color: '#15803d', marginBottom: '0.75rem' }}>
                                        Upload Lichess database with 3M+ puzzles
                                    </p>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setImportingPuzzles(true);
                                                try {
                                                    toast.info('Importing puzzles from CSV... This may take a minute.');
                                                    await importLichessCSVFile(file, serviceProviderId, loggedInUserId, 100);
                                                    toast.success('Puzzles imported successfully!');
                                                    // Reload puzzles after import
                                                    if (activeCategory !== 'import') {
                                                        await loadPuzzles();
                                                    }
                                                } catch (error) {
                                                    console.error('CSV import error:', error);
                                                    toast.error('CSV import failed');
                                                } finally {
                                                    setImportingPuzzles(false);
                                                }
                                            }
                                        }}
                                        disabled={importingPuzzles}
                                        style={{
                                            width: '100%',
                                            fontSize: '0.75rem',
                                            padding: '0.5rem',
                                            border: '1px solid #86efac',
                                            borderRadius: '0.375rem',
                                            cursor: importingPuzzles ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <a
                                            href="https://database.lichess.org/#puzzles"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: '0.75rem',
                                                color: '#166534',
                                                textDecoration: 'underline',
                                                fontWeight: 600
                                            }}
                                        >
                                            📥 Download Lichess Database →
                                        </a>
                                        <p style={{ fontSize: '0.7rem', color: '#15803d', marginTop: '0.25rem' }}>
                                            File: <code style={{ background: 'white', padding: '0.125rem 0.25rem', borderRadius: '0.25rem' }}>lichess_db_puzzle.csv.zst</code>
                                        </p>
                                </div>
                                </div>

                                <div style={{ 
                                    padding: '1rem', 
                                    background: '#fef3c7', 
                                    borderRadius: '0.5rem',
                                    border: '1px solid #fde047'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: 0 }}>
                                        🌱 Seed Sample Puzzles
                                    </p>
                                        {totalPuzzleCount > 0 && (
                                            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#78350f', margin: 0 }}>
                                                Total: {totalPuzzleCount} puzzles
                                            </p>
                                        )}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.75rem' }}>
                                        Import 616 pre-generated puzzles (88 per category)
                                    </p>
                                    
                                    {/* Progress Bar */}
                                    {seedingPuzzles && seedingProgress.total > 0 && (
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                marginBottom: '0.25rem'
                                            }}>
                                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 500 }}>
                                                    Seeding puzzles...
                                                </span>
                                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 600 }}>
                                                    {seedingProgress.current} / {seedingProgress.total}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '100%',
                                                height: '8px',
                                                background: '#fde047',
                                                borderRadius: '4px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${(seedingProgress.current / seedingProgress.total) * 100}%`,
                                                    height: '100%',
                                                    background: 'linear-gradient(to right, #f59e0b, #d97706)',
                                                    transition: 'width 0.3s ease',
                                                    borderRadius: '4px'
                                                }} />
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <button
                                            onClick={handleSeedPuzzles}
                                            disabled={seedingPuzzles || importingPuzzles}
                                            style={{
                                                flex: 1,
                                                padding: '0.5rem',
                                                background: seedingPuzzles ? '#9ca3af' : '#f59e0b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                cursor: seedingPuzzles ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {seedingPuzzles ? `🌱 Seeding... ${seedingProgress.current}/${seedingProgress.total}` : '🌱 Seed 725 Perfect Puzzles'}
                                        </button>
                                        
                                        <label style={{ flex: 1 }}>
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        setSeedingPuzzles(true);
                                                        setSeedingProgress({ current: 0, total: 0 });
                                                        try {
                                                            const csvContent = await file.text();
                                                            const result = await seedAdvancedPuzzlesFromCSVContent(
                                                                serviceProviderId || '',
                                                                loggedInUserId || '',
                                                                csvContent,
                                                                (current, total) => {
                                                                    setSeedingProgress({ current, total });
                                                                },
                                                                false // Don't delete existing puzzles
                                                            );
                                                            if (result.success) {
                                                                await loadPuzzles();
                                                                await loadTotalPuzzleCount();
                                                            }
                                                        } catch (error) {
                                                            console.error('CSV import error:', error);
                                                            toast.error('Failed to import CSV file');
                                                        } finally {
                                                            setSeedingPuzzles(false);
                                                            setSeedingProgress({ current: 0, total: 0 });
                                                            // Reset file input
                                                            e.target.value = '';
                                                        }
                                                    }
                                                }}
                                                disabled={seedingPuzzles || importingPuzzles}
                                                style={{ display: 'none' }}
                                            />
                                            <div style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                background: (seedingPuzzles || importingPuzzles) ? '#9ca3af' : '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                cursor: (seedingPuzzles || importingPuzzles) ? 'not-allowed' : 'pointer',
                                                textAlign: 'center'
                                            }}>
                                                📤 Import CSV (Max 200)
                                            </div>
                                        </label>
                                    </div>
                                    
                                    {seedingPuzzles && seedingProgress.total > 0 && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                marginBottom: '0.25rem'
                                            }}>
                                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 500 }}>
                                                    Importing puzzles...
                                                </span>
                                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 600 }}>
                                                    {seedingProgress.current} / {seedingProgress.total}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '100%',
                                                height: '6px',
                                                background: '#fde047',
                                                borderRadius: '3px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${(seedingProgress.current / seedingProgress.total) * 100}%`,
                                                    height: '100%',
                                                    background: 'linear-gradient(to right, #10b981, #059669)',
                                                    transition: 'width 0.3s ease',
                                                    borderRadius: '3px'
                                                }} />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ 
                                    padding: '1rem', 
                                    background: '#dbeafe', 
                                    borderRadius: '0.5rem',
                                    border: '1px solid #93c5fd'
                                }}>
                                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' }}>
                                        📚 How to Get Puzzles:
                                    </p>
                                    <ul style={{ fontSize: '0.7rem', color: '#1e3a8a', listStyle: 'disc', paddingLeft: '1.25rem', lineHeight: '1.6' }}>
                                        <li><strong>Import CSV:</strong> Upload custom puzzles from CSV (max 200 per import)</li>
                                        <li><strong>Seed Puzzles:</strong> Quick start with 725 pre-made puzzles</li>
                                        <li><strong>Create Tab:</strong> Save current board setup as puzzle</li>
                                    </ul>
                                </div>
                            </div>
                    ) : !selectedPuzzle ? (
                        // Puzzle List View - CheQuered Quest
                        <div className="puzzle-list" style={{ padding: '2rem', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                            <div className="puzzle-list-header" style={{ marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <div>
                                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                            {categories.find((m) => m.id === activeCategory)?.label} Training
                                        </h2>
                                        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                            {totalPuzzleCount > 0 && `${totalPuzzleCount} total puzzles • `}
                                            {filteredPuzzles.length} showing
                                        </p>
                                    </div>
                                    {isAdmin && filteredPuzzles.length > 0 && (
                                        <button
                                            onClick={handleDeleteAllPuzzles}
                    style={{
                                                padding: '0.5rem 1rem',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                        display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                            title="Delete all puzzles (Admin only)"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete All
                                        </button>
                        )}
                                </div>
                                
                                {/* Search Input */}
                                <div className="search-filter" style={{ marginBottom: '1rem' }}>
                                    <input
                                        type="text"
                                        placeholder="Search puzzles by description, themes, or hint..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                                width: '100%',
                                            padding: '0.625rem 1rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.875rem',
                        background: 'white',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#9333ea'}
                                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                    />
                                </div>

                                {/* Complexity Filter - Choice Chips */}
                                <div className="difficulty-filter" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                                        Filter by difficulty:
                                    </label>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        {(['all', 'beginner', 'intermediate', 'advanced'] as ComplexityLevel[]).map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => setSelectedComplexity(level)}
                                        style={{
                                                    padding: '0.5rem 1rem',
                                                    border: `2px solid ${selectedComplexity === level ? '#9333ea' : '#d1d5db'}`,
                                                    borderRadius: '0.5rem',
                                            fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    background: selectedComplexity === level ? '#f3e8ff' : 'white',
                                                    color: selectedComplexity === level ? '#7c3aed' : '#374151',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    textTransform: 'capitalize'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (selectedComplexity !== level) {
                                                        e.currentTarget.style.borderColor = '#9333ea';
                                                        e.currentTarget.style.background = '#faf5ff';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedComplexity !== level) {
                                                        e.currentTarget.style.borderColor = '#d1d5db';
                                                        e.currentTarget.style.background = 'white';
                                                    }
                                                }}
                                            >
                                                {level === 'all' ? 'All' : level}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Bulk Selection Controls */}
                                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <button
                                            onClick={toggleBulkSelection}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: bulkSelectionMode ? '#9333ea' : 'white',
                                                color: bulkSelectionMode ? 'white' : '#374151',
                                                border: `2px solid ${bulkSelectionMode ? '#9333ea' : '#d1d5db'}`,
                                                borderRadius: '0.5rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {bulkSelectionMode ? '✓ Bulk Mode' : '☐ Bulk Select'}
                                        </button>
                                        
                                        {bulkSelectionMode && (
                                            <>
                                                <button
                                                    onClick={selectAllFiltered}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        background: 'white',
                                                        color: '#374151',
                                                        border: '2px solid #d1d5db',
                                                        borderRadius: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Select All ({filteredPuzzles.length})
                                                </button>
                                                <button
                                                    onClick={clearSelection}
                                                    disabled={selectedPuzzleIds.size === 0}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        background: 'white',
                                                        color: '#374151',
                                                        border: '2px solid #d1d5db',
                                                        borderRadius: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        cursor: selectedPuzzleIds.size === 0 ? 'not-allowed' : 'pointer',
                                                        opacity: selectedPuzzleIds.size === 0 ? 0.5 : 1
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    onClick={bulkAddToSubscription}
                                                    disabled={selectedPuzzleIds.size === 0}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        background: selectedPuzzleIds.size === 0 ? '#9ca3af' : '#10b981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        cursor: selectedPuzzleIds.size === 0 ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    Add {selectedPuzzleIds.size > 0 ? `${selectedPuzzleIds.size}` : ''} to Subscription
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            ) : (
                            <>
                            {loadingPuzzles ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                                    <p style={{ fontSize: '0.875rem' }}>Loading puzzles...</p>
                                </div>
                            ) : filteredPuzzles.length === 0 ? (
                                <div className="empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>♟️</div>
                                    <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No puzzles available in this module yet.</p>
                                    {SHOW_IMPORT_PUZZLES && (
                                    <button 
                                        onClick={() => setActiveCategory('import')}
                                        style={{
                                            marginTop: '1rem',
                                            padding: '0.5rem 1rem',
                                            background: '#9333ea',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Import Puzzles
                                    </button>
                                    )}
                                </div>
                            ) : (
                                <div className="puzzles-grid" style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                    gap: '0.75rem'
                                }}>
                                    {filteredPuzzles.map((puzzle) => (
                                        <PuzzleListItem
                                            key={puzzle.id}
                                            puzzle={puzzle}
                                            isSelected={selectedPuzzle?.id === puzzle.id}
                                            isBookmarked={bookmarkedPuzzleIds.includes(puzzle.id)}
                                            onSelect={handlePuzzleSelect}
                                            onBookmark={handleBookmarkToggle}
                                            onDelete={handleDeletePuzzle}
                                            canDelete={isAdmin}
                                            onAddToSubscription={handleAddToSubscription}
                                            isInSubscription={puzzlesInSubscription.has(puzzle.id)}
                                            showAddButton={true}
                                            bulkSelectionMode={bulkSelectionMode}
                                            isChecked={selectedPuzzleIds.has(puzzle.id)}
                                            onToggleCheck={togglePuzzleSelection}
                                        />
                                    ))}
                                </div>
                            )}
                            </>
                        </div>
                    ) : (
                        // Puzzle Viewer
                        <div className="puzzle-viewer" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="puzzle-header-bar" style={{ 
                                padding: '1rem 1.5rem', 
                                borderBottom: '1px solid #e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: '#f9fafb',
                                flexShrink: 0
                            }}>
                                <div className="puzzle-title-section" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0, padding: 0 }}>
                                        {selectedPuzzle?.description || 'Puzzle'}
                                    </h2>
                                </div>
                            </div>
                            <div style={{ 
                                flex: 1, 
                                overflow: 'auto', 
                                minHeight: 0,
                                minWidth: 0,
                                padding: '1rem',
                                margin: 0,
                                display: 'flex',
                                width: '100%',
                                height: '100%',
                                background: 'transparent',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                <div style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative'
                                }}>
                        <ChequeredChess
                            subscriptionId={subscriptionId}
                            subscriptionName="Chess Practice"
                            userRole={activeRole?.role as 'teacher' | 'student' | undefined}
                            isInMeeting={isInMeeting}
                            participantName={activeRole?.displayName || 'Player'}
                            room={room}
                            disableTeachingMode={false}
                            initialPuzzle={selectedPuzzle}
                            initialMode={'puzzle'}
                            onPuzzleSolved={selectedPuzzle && serviceProviderId && loggedInUserId ? () => {
                                trackChequeredPuzzleSolved({
                                    service_provider_id: serviceProviderId,
                                    user_id: loggedInUserId,
                                    subscription_id: subscriptionId,
                                    subscription_name: subscriptionName || undefined,
                                    user_name: activeRole?.displayName || undefined,
                                    user_role: activeRole?.role || undefined,
                                    puzzle_id: selectedPuzzle.id
                                });
                            } : undefined}
                        />
                </div>
                            </div>
                        </div>
                    )}
                </motion.main>
            </motion.div>
        </div>
        {renderSubscriptionDialog()}

        {/* Class Puzzles Guide Modal */}
        {showClassPuzzlesGuide && (
            <Dialog 
                open={showClassPuzzlesGuide} 
                onClose={() => setShowClassPuzzlesGuide(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle style={{ 
                    background: 'linear-gradient(to right, #9333ea, #3b82f6)', 
                    color: 'white', 
                    padding: '1.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>📊 Class Puzzles Guide</span>
                    <IconButton onClick={() => setShowClassPuzzlesGuide(false)} style={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent style={{ padding: '2rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        {/* Grid View */}
                        <section>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                🎯 Grid View
                            </h3>
                            <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem' }}>
                                <p style={{ color: '#374151', marginBottom: '0.75rem' }}>
                                    All puzzles are displayed in a <strong>beautiful grid layout</strong> with cards showing:
                                </p>
                                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#374151', lineHeight: '1.8' }}>
                                    <li>Puzzle description and category</li>
                                    <li>Complexity badge (Beginner/Intermediate/Advanced)</li>
                                    <li>Rating and category tags</li>
                                    <li>Status badges (Solved ✓ or Taught 📚)</li>
                                    <li>Hint preview</li>
                                </ul>
                            </div>
                        </section>

                        {/* Filtering */}
                        <section>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                🔍 Filtering Puzzles
                            </h3>
                            <div style={{ background: '#eff6ff', padding: '1rem', borderRadius: '0.5rem' }}>
                                <p style={{ color: '#1e40af', marginBottom: '0.75rem', fontWeight: 600 }}>
                                    Use filter buttons to view:
                                </p>
                                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#1e3a8a', lineHeight: '1.8' }}>
                                    <li><strong>All Puzzles</strong> - View everything</li>
                                    <li><strong>Unsolved</strong> - Puzzles not yet completed or taught</li>
                                    <li><strong>Solved</strong> - Completed puzzles (green border)</li>
                                    <li><strong>Taught</strong> - Puzzles marked as taught (blue border)</li>
                                </ul>
                            </div>
                        </section>

                        {/* Puzzle Cards Actions */}
                        <section>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                🎮 Puzzle Card Actions
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                                <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #86efac' }}>
                                    <div style={{ fontWeight: 700, color: '#166534', marginBottom: '0.5rem' }}>🔵 Solve Button</div>
                                    <p style={{ fontSize: '0.875rem', color: '#15803d' }}>
                                        Opens puzzle in a modal with interactive chess board. Click to solve!
                                    </p>
                                </div>
                                <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #86efac' }}>
                                    <div style={{ fontWeight: 700, color: '#166534', marginBottom: '0.5rem' }}>✓ Solved Button</div>
                                    <p style={{ fontSize: '0.875rem', color: '#15803d' }}>
                                        Mark puzzle as completed. Only shows if not already solved.
                                    </p>
                                </div>
                                <div style={{ background: '#fef2f2', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fca5a5' }}>
                                    <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: '0.5rem' }}>🗑️ Delete Button</div>
                                    <p style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                                        Remove puzzle from this class. Confirmation required.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Bulk Actions */}
                        <section>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                📦 Bulk Actions
                            </h3>
                            <div style={{ background: '#fef3c7', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fde047' }}>
                                <p style={{ color: '#92400e', marginBottom: '0.75rem', fontWeight: 600 }}>
                                    Delete All Button (Top Right):
                                </p>
                                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#78350f', lineHeight: '1.8' }}>
                                    <li>Only visible to administrators</li>
                                    <li>Removes all puzzles from the current class</li>
                                    <li>Requires confirmation before deleting</li>
                                    <li>Shows count: "Delete all X puzzles from this class?"</li>
                                </ul>
                            </div>
                        </section>

                        {/* Stats Bar */}
                        <section>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                📈 Statistics Bar
                            </h3>
                            <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem' }}>
                                <p style={{ color: '#374151', marginBottom: '0.75rem' }}>
                                    The top bar shows real-time statistics:
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.875rem' }}>
                                    <span style={{ color: '#111827', fontWeight: 600 }}>Total: X</span>
                                    <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Solved: X</span>
                                    <span style={{ color: '#3b82f6', fontWeight: 600 }}>📚 Taught: X</span>
                                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>⏳ Pending: X</span>
                                </div>
                            </div>
                        </section>

                        {/* Tips */}
                        <section style={{ background: 'linear-gradient(to right, #ede9fe, #dbeafe)', padding: '1.5rem', borderRadius: '0.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                                💡 Pro Tips
                            </h3>
                            <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#374151', lineHeight: '1.8' }}>
                                <li>Click on any puzzle card to open it in a modal</li>
                                <li>Use filters to quickly find unsolved puzzles</li>
                                <li>Mark puzzles as "Taught" after teaching them in class</li>
                                <li>Delete individual puzzles you no longer need</li>
                                <li>Use "Delete All" to clear the class and start fresh</li>
                            </ul>
                        </section>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
                    <Button
                        onClick={() => setShowClassPuzzlesGuide(false)}
                        variant="contained"
                        style={{ background: 'linear-gradient(to right, #9333ea, #3b82f6)', color: 'white' }}
                    >
                        Got it!
                    </Button>
                </DialogActions>
            </Dialog>
        )}

        {/* Import Puzzles Dialog - Hidden but code preserved */}
        {SHOW_IMPORT_PUZZLES && (
        <Dialog
            open={showImportDialog}
            onClose={() => setShowImportDialog(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '0.75rem'
                }
            }}
        >
            <DialogTitle style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb',
                padding: '1rem 1.5rem'
            }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>📥 Import Puzzles</span>
                <IconButton
                    onClick={() => setShowImportDialog(false)}
                    style={{ color: '#6b7280', padding: '0.5rem' }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Custom Puzzles - Excel Import */}
                    <div style={{ 
                        padding: '1rem', 
                        background: '#dbeafe', 
                        borderRadius: '0.5rem',
                        border: '2px solid #93c5fd'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e40af', margin: 0 }}>
                                📊 Custom Puzzles Import (Excel)
                            </p>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#1e3a8a', marginBottom: '0.75rem' }}>
                            Download template, fill with your custom puzzles, and import back
                        </p>
                        
                        {/* Excel Import Progress Bar */}
                        {importingExcel && excelImportProgress.total > 0 && (
                            <div style={{ marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#1e3a8a', fontWeight: 500 }}>Importing...</span>
                                    <span style={{ fontSize: '0.75rem', color: '#1e3a8a', fontWeight: 600 }}>
                                        {excelImportProgress.current} / {excelImportProgress.total}
                                    </span>
                                </div>
                                <div style={{ width: '100%', height: '8px', background: '#93c5fd', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${(excelImportProgress.current / excelImportProgress.total) * 100}%`,
                                        height: '100%',
                                        background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                                        transition: 'width 0.3s ease',
                                        borderRadius: '4px'
                                    }} />
                                </div>
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                onClick={() => {
                                    if (serviceProviderId && loggedInUserId) {
                                        trackChequeredTemplateDownloaded({
                                            service_provider_id: serviceProviderId,
                                            user_id: loggedInUserId,
                                            subscription_id: subscriptionId,
                                            subscription_name: subscriptionName || undefined,
                                            user_name: activeRole?.displayName || undefined,
                                            user_role: activeRole?.role || undefined,
                                            template_type: 'excel'
                                        });
                                    }
                                    downloadPuzzleTemplate();
                                }}
                                disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem',
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                📥 Download Excel Template
                            </button>
                            <label style={{ flex: 1 }}>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleExcelImport}
                                    disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                    style={{ display: 'none' }}
                                />
                                <div style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    background: importingExcel ? '#9ca3af' : '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: importingExcel ? 'not-allowed' : 'pointer',
                                    textAlign: 'center'
                                }}>
                                    {importingExcel ? '📤 Importing...' : '📤 Import Excel'}
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Custom Puzzles - CSV Import */}
                    <div style={{ 
                        padding: '1rem', 
                        background: '#dcfce7', 
                        borderRadius: '0.5rem',
                        border: '2px solid #86efac'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#166534', margin: 0 }}>
                                📊 Custom Puzzles Import (CSV)
                            </p>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#15803d', marginBottom: '0.5rem' }}>
                            Download CSV template, fill with your custom puzzles, and import back
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#059669', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                            📋 Format: FEN, Moves, Rating, Complexity, Category, Description, Hint, Themes
                            <br />
                            💡 The template includes example rows. See CSV_PUZZLE_IMPORT_FORMAT.md for detailed format guide.
                        </p>
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                onClick={() => {
                                    if (serviceProviderId && loggedInUserId) {
                                        trackChequeredTemplateDownloaded({
                                            service_provider_id: serviceProviderId,
                                            user_id: loggedInUserId,
                                            subscription_id: subscriptionId,
                                            subscription_name: subscriptionName || undefined,
                                            user_name: activeRole?.displayName || undefined,
                                            user_role: activeRole?.role || undefined,
                                            template_type: 'csv'
                                        });
                                    }
                                    downloadCSVTemplate();
                                }}
                                disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                📥 Download CSV Template
                            </button>
                            <label style={{ flex: 1 }}>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            if (serviceProviderId && loggedInUserId) {
                                                trackChequeredImportClicked({
                                                    service_provider_id: serviceProviderId,
                                                    user_id: loggedInUserId,
                                                    subscription_id: subscriptionId,
                                                    subscription_name: subscriptionName || undefined,
                                                    user_name: activeRole?.displayName || undefined,
                                                    user_role: activeRole?.role || undefined,
                                                    import_type: 'csv'
                                                });
                                            }
                                            setImportingPuzzles(true);
                                            let csvResult: { success: boolean; count: number } = { success: false, count: 0 };
                                            try {
                                                const result = await importPuzzlesFromCSVFile(
                                                    file,
                                                    serviceProviderId,
                                                    loggedInUserId,
                                                    (current, total) => {
                                                        setExcelImportProgress({ current, total });
                                                    }
                                                );
                                                csvResult = result;
                                                if (result.success) {
                                                    await loadPuzzles();
                                                    await loadTotalPuzzleCount();
                                                }
                                            } catch (error) {
                                                console.error('CSV import error:', error);
                                            } finally {
                                                setImportingPuzzles(false);
                                                setExcelImportProgress({ current: 0, total: 0 });
                                                if (serviceProviderId && loggedInUserId) {
                                                    trackChequeredImportCompleted({
                                                        service_provider_id: serviceProviderId,
                                                        user_id: loggedInUserId,
                                                        subscription_id: subscriptionId,
                                                        subscription_name: subscriptionName || undefined,
                                                        user_name: activeRole?.displayName || undefined,
                                                        user_role: activeRole?.role || undefined,
                                                        import_type: 'csv',
                                                        count: csvResult.count,
                                                        success: csvResult.success
                                                    });
                                                }
                                            }
                                        }
                                    }}
                                    disabled={seedingPuzzles || importingPuzzles || importingExcel}
                                    style={{ display: 'none' }}
                                />
                                <div style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    background: importingPuzzles ? '#9ca3af' : '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: importingPuzzles ? 'not-allowed' : 'pointer',
                                    textAlign: 'center'
                                }}>
                                    {importingPuzzles ? '📤 Importing...' : '📤 Import CSV'}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
                <Button
                    onClick={() => setShowImportDialog(false)}
                    variant="contained"
                    style={{ background: '#9333ea', color: 'white' }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        )}

        {/* Create Puzzle Dialog */}
        <Dialog
            open={showCreatePuzzleModal}
            onClose={() => setShowCreatePuzzleModal(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '0.75rem',
                    maxHeight: '95vh'
                }
            }}
        >
            <DialogTitle style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb',
                padding: '1rem 1.5rem'
            }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>✨ Create Custom Puzzle</span>
                <IconButton
                    onClick={() => setShowCreatePuzzleModal(false)}
                    style={{ color: '#6b7280', padding: '0.5rem' }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ 
                padding: '0',
                overflow: 'auto',
                maxHeight: 'calc(95vh - 140px)'
            }}>
                <div style={{ padding: '1.5rem' }}>
                    <CreatePuzzleView
                        serviceProviderId={serviceProviderId}
                        loggedInUserId={loggedInUserId}
                    />
                </div>
            </DialogContent>
            <DialogActions style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
                <Button
                    onClick={() => {
                        setShowCreatePuzzleModal(false);
                        // Reload puzzles after creating
                        loadPuzzles();
                        loadTotalPuzzleCount();
                    }}
                    variant="contained"
                    style={{ background: '#10b981', color: 'white' }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ChequeredPage;
